export type AppLanguage = 'en' | 'fr' | 'it';

interface TranslationNode {
  [key: string]: string | TranslationNode;
}

export const translations: Record<AppLanguage, TranslationNode> = {
  en: {
    app: {
      title: 'CRM Agentique',
      unauthenticated: {
        eyebrow: 'Business transformation',
        prompt: 'Please sign in before continuing.'
      },
      sessionExpired: {
        eyebrow: 'Session expired',
        title: 'You are no longer connected',
        prompt: 'Reconnect using one of the sign-in buttons below. Local drafts remain available on this device.'
      }
    },
    common: {
      languages: {
        english: 'English',
        french: 'French',
        italian: 'Italian'
      },
      themes: {
        light: 'Light',
        dark: 'Dark',
        ninon: 'Ninon'
      },
      labels: {
        assistant: 'Assistant',
        unavailable: 'Unavailable',
        unknown: 'Unknown',
        notSpecified: 'Not specified',
        notTracked: 'Not tracked',
        mainContact: 'Main contact',
        selected: 'Selected',
        selectedGroup: 'Selected group',
        allGroups: 'All groups',
        noGroupAssigned: 'No group assigned',
        noMainContact: 'No main contact',
        unnamedCompany: 'Unnamed company',
        unnamedContact: 'Unnamed contact',
        unnamedOffering: 'Unnamed offering',
        locationUnavailable: 'Location unavailable',
        revenueUnavailable: 'Revenue unavailable',
        websiteUnavailable: 'Website unavailable',
        phoneUnavailable: 'Phone unavailable',
        linkedInUnavailable: 'LinkedIn unavailable',
        unknownStatus: 'Unknown status',
        unknownSource: 'Unknown source',
        unknownRelationship: 'Unknown relationship',
        unknownType: 'Unknown type',
        unknownCompany: 'Unknown company',
        unknownContact: 'Unknown contact',
        openEnded: 'Open-ended',
        startDateUnavailable: 'Start date unavailable',
        noEmailAddress: 'No email address',
        noActionScheduled: 'No action scheduled',
        active: 'Active',
        disabled: 'Disabled',
        available: 'Available',
        missing: 'Missing',
        draftRestored: 'Local draft restored.',
        requiredField: '{{label}} *',
        withCount: '{{label}} ({{count}})'
      },
      actions: {
        addAction: 'Add action',
        addComment: 'Add comment',
        addNote: 'Add note',
        addCustomer: 'Add customer',
        addContract: 'Add contract',
        addOffering: 'Add offering',
        admin: 'Admin',
        backToCompanies: 'Back to companies',
        backToHome: 'Back to home',
        cancel: 'Cancel',
        close: 'Close',
        createGroup: 'Create group',
        delete: 'Delete',
        detail: 'Détail',
        edit: 'Edit',
        logout: 'Logout',
        refresh: 'Refresh',
        refreshOfferings: 'Refresh offerings',
        saveAction: 'Save action',
        saveComment: 'Save note',
        saveContract: 'Save contract',
        saveCustomer: 'Save customer',
        send: 'Send',
        setAsMainContact: 'Set as main contact',
        saving: 'Saving...',
        viewDetails: 'View details'
      },
      confirm: {
        deleteAction: 'Delete this action from the company?',
        deleteComment: 'Delete this comment from the company?',
        deleteNote: 'Delete this note from the company?',
        deleteRex: 'Delete this REX from the company?',
        deleteCustomer: 'Delete this customer from the company?',
        deleteContract: 'Delete this contract from the company?',
        deleteOffering: 'Delete this offering from the catalog?',
        deleteCompany: 'Delete "{{companyName}}" from the CRM? All linked customers, contracts, actions, notes and documents will be deleted.'
      },
      counts: {
        companies: {
          one: '{{count}} company',
          other: '{{count}} companies'
        },
        customers: {
          one: '{{count}} customer',
          other: '{{count}} customers'
        },
        contacts: {
          one: '{{count}} contact',
          other: '{{count}} contacts'
        },
        activeCustomers: {
          one: '{{count}} active customer',
          other: '{{count}} active customers'
        },
        offerings: {
          one: '{{count}} offering',
          other: '{{count}} offerings'
        },
        qualifiedCompanies: {
          one: '{{count}} qualified company',
          other: '{{count}} qualified companies'
        },
        contracts: {
          one: '{{count}} contract',
          other: '{{count}} contracts'
        },
        activeContracts: {
          one: '{{count}} active contract',
          other: '{{count}} active contracts'
        },
        openActions: {
          one: '{{count}} open action',
          other: '{{count}} open actions'
        },
        actions: {
          one: '{{count}} action',
          other: '{{count}} actions'
        },
        comments: {
          one: '{{count}} comment',
          other: '{{count}} comments'
        },
        notes: {
          one: '{{count}} note',
          other: '{{count}} notes'
        },
        messages: {
          one: '{{count}} message',
          other: '{{count}} messages'
        },
        years: {
          one: '{{count}} year',
          other: '{{count}} years'
        },
        documents: {
          one: '{{count}} document',
          other: '{{count}} documents'
        },
        webLinks: {
          one: '{{count}} web link',
          other: '{{count}} web links'
        },
        rex: {
          one: '{{count}} REX',
          other: '{{count}} REX'
        },
        groups: {
          one: '{{count}} group',
          other: '{{count}} groups'
        },
        members: {
          one: '{{count}} member',
          other: '{{count}} members'
        },
        admins: {
          one: '{{count}} admin',
          other: '{{count}} admins'
        },
        adminSlots: {
          one: '{{count}} admin slot',
          other: '{{count}} admin slots'
        },
        days: {
          one: '{{count}} day',
          other: '{{count}} days'
        }
      },
      enums: {
        companyStatus: {
          PROSPECT: 'Prospect',
          QUALIFIED: 'Qualified',
          ACTIVE_CUSTOMER: 'Active customer',
          PARTNER: 'Partner',
          LOST: 'Lost'
        },
        leadSource: {
          WEBSITE: 'Website',
          REFERRAL: 'Referral',
          OUTBOUND: 'Outbound',
          EVENT: 'Event',
          LINKEDIN: 'LinkedIn',
          OTHER: 'Other'
        },
        relationshipStatus: {
          PROSPECT: 'Prospect',
          CUSTOMER: 'Customer',
          OLD_RELATIONSHIP: 'Old relationship'
        },
        offeringType: {
          FIXED_PRICE: 'Fixed price',
          TIME_AND_MATERIAL: 'Time and material'
        },
        noteType: {
          COMMENT: 'Comment',
          REFLECTION: 'Reflection',
          OPPORTUNITY: 'Opportunity to explore'
        },
        actionType: {
          CALL: 'Call',
          EMAIL: 'Email',
          MEETING: 'Meeting',
          FOLLOW_UP: 'Follow-up',
          TODO: 'Task',
          LINKEDIN_OUTREACH: 'LinkedIn outreach',
          PREPARE_PROPOSAL: 'Prepare proposal',
          SEND_PROPOSAL: 'Send proposal',
          NEGOTIATE_CONTRACT: 'Negotiate contract',
          RENEWAL_CHECK: 'Renewal check',
          UPSELL_CHECK: 'Upsell check',
          INTERNAL_TASK: 'Internal task'
        },
        actionStatus: {
          OPEN: 'Open',
          IN_PROGRESS: 'In progress',
          DONE: 'Done',
          CANCELLED: 'Cancelled',
          BLOCKED: 'Blocked',
          SNOOZED: 'Snoozed',
          OVERDUE: 'Overdue'
        },
        actionPriority: {
          LOW: 'Low',
          NORMAL: 'Normal',
          HIGH: 'High',
          URGENT: 'Urgent'
        },
        previewStatus: {
          NEW: 'New',
          QUALIFIED: 'Qualified',
          FOLLOW_UP: 'Follow-up',
          CONVERTED: 'Converted'
        },
        previewPriority: {
          LOW: 'Low',
          NORMAL: 'Normal',
          HIGH: 'High'
        },
        previewChannel: {
          email: 'Email',
          phone: 'Phone',
          meet: 'Video call'
        }
      }
    },
    mainMenu: {
      home: 'Home',
      loadingGroups: 'Loading groups...',
      noGroupsAvailable: 'No groups available',
      signInProviders: 'Sign in providers',
      signInWithGoogle: 'Sign in with Google',
      signInWithMicrosoft: 'Sign in with Microsoft',
      styleGuide: 'Style guide',
      languageSelector: 'Language selector',
      themeSelector: 'Theme selector'
    },
    home: {
      tabs: {
        dashboard: 'Dashboard',
        groupPortfolio: 'Customer portfolio',
        groupMembers: 'Group members',
        groupAnalysis: 'Portfolio analysis',
        offerings: 'Offerings'
      },
      hero: {
        eyebrow: 'Sales cockpit',
        title: 'Focus on the accounts that need action now',
        copy: 'Start with the active workload, keep an eye on qualified opportunities, and move from the homepage straight into the account that needs attention.',
        summaryAria: 'Company portfolio summary'
      },
      focus: {
        title: 'Accounts needing attention',
        subtitle: 'A short list of accounts ranked by open work, live delivery and commercial potential.',
        loading: 'Loading priority accounts...',
        emptyTitle: 'No account requires attention right now.',
        emptyDetail: 'Create companies or switch group to populate this focus list.',
        openCompanyAria: 'Open {{companyName}}',
        summaryAria: 'Priority account summary',
        priorityOpenActions: 'Follow-up in progress',
        priorityActiveContracts: 'Live account to protect',
        priorityQualified: 'Qualified opportunity to push',
        priorityCoverage: 'Coverage to build'
      },
      actionTimeline: {
        title: 'Action timeline',
        subtitle: 'Your open actions that need attention now, grouped by execution horizon.',
        loading: 'Loading action timeline...',
        emptyTitle: 'No urgent actions are visible right now.',
        emptyDetail: 'Your assigned actions with overdue, today or this-week dates will appear here.',
        emptyBucket: 'No actions in this horizon.',
        overdue: 'Overdue',
        today: 'Today',
        thisWeek: 'This week',
        dueLabel: 'Due {{date}}',
        scheduledLabel: 'Scheduled {{date}}',
        noDate: 'No due date',
        openAccount: 'Open account'
      },
      opportunities: {
        title: 'Opportunity notes',
        subtitle: 'Recent commercial opportunities captured across the visible account portfolio.',
        loading: 'Loading opportunity notes...',
        emptyTitle: 'No opportunity note is visible right now.',
        emptyDetail: 'Create opportunity notes from account pages to surface them here.',
        companyWide: 'Company-wide note',
        openOpportunityAria: 'Open {{companyName}} opportunity notes'
      },
      offerings: {
        title: 'Offerings catalog',
        subtitle: 'Independent commercial offerings available in the CRM.',
        toolbarEyebrow: 'Commercial catalog',
        toolbarCopy: 'Review the current offering catalog, then open any offering in a dialog to create it, update it or manage its attached files.',
        newHeading: 'New offering',
        createLabel: 'Create offering',
        detailHeading: 'Offering details',
        editHeading: 'Edit offering',
        loading: 'Loading offerings...',
        loadingEditor: 'Loading offering dialog...',
        loadingEditorDetail: 'Fetching the latest offering details from the CRM.',
        emptyTitle: 'No offerings are available yet.',
        emptyDetail: 'Create an offering from this catalog, then reopen it to attach documents.',
        missingDescription: 'No offering description is available yet.',
        workspaceTitle: 'Select an offering to inspect or edit it.',
        workspaceDetail: 'The selected offering stays open here while the catalog remains visible.',
        actionFailed: 'Offering action failed.',
        loadError: 'Unable to load offering details.',
        createError: 'Unable to create offering.',
        updateError: 'Unable to update offering.',
        deleteError: 'Unable to delete offering.',
        refreshing: 'Refreshing...'
      },
      companies: {
        title: 'Customer portfolio',
        subtitle: 'Visible client accounts with search, status and tag filters, plus sortable commercial signals.',
        toolbarEyebrow: 'Client portfolio',
        toolbarCopy: 'Search the visible portfolio, combine status and tag filters, and rank accounts by delivery load, coverage, name or contract revenue.',
        filteredCopy: '{{count}} client account(s) match the current filters.',
        loading: 'Loading companies...',
        emptyTitle: 'No companies are available yet.',
        emptyDetail: 'Create companies from the CRM tools, then refresh this view.',
        noResultsTitle: 'No client account matches the current filters.',
        noResultsDetail: 'Adjust the search term, selected statuses, selected tags or sort to widen the portfolio.',
        searchPlaceholder: 'Search a client',
        statusFilterLabel: 'Status',
        tagFilterLabel: 'Tags',
        sortLabel: 'Sort by',
        sortPriority: 'Active work and coverage',
        sortName: 'Name',
        sortContractRevenue: 'Contract revenue',
        industry: 'Industry',
        employees: 'Employees',
        revenue: 'Revenue',
        contractRevenue: 'Contract revenue',
        source: 'Source',
        tagsAria: 'Client account tags',
        openCompanyAria: 'Open details for {{companyName}}'
      },
      groupMembers: {
        title: 'Group members',
        subtitle: '{{memberCount}} visible in the selected group, with their current contract staffing.',
        toolbarEyebrow: 'Team skills',
        toolbarCopy: 'Filter the selected group by skill tags to focus on the right people.',
        filteredCopy: '{{count}} members match the selected skill tags.',
        tagFilterLabel: 'Skill tags',
        loading: 'Loading group members...',
        emptyTitle: 'No group members are visible yet.',
        emptyDetail: 'Select a group with users to display staffing and bench.',
        noResultsTitle: 'No group members match the selected skill tags.',
        noResultsDetail: 'Adjust or clear the current tag filter to widen the member list.',
        benchTitle: 'Bench',
        benchSubtitle: 'Members without an active contract right now.',
        staffedTitle: 'On assignment',
        staffedSubtitle: 'Members currently staffed on active contracts.',
        emptyBench: 'Nobody is currently on the bench.',
        emptyStaffed: 'No active contract assignment is visible in this group.',
        skillTags: 'Skill tags',
        staffedCount: '{{count}} staffed',
        benchCount: '{{count}} on bench',
        contractCount: '{{count}} contract(s)',
        contractMeta: '{{companyName}} · {{contractLabel}}',
        contractDateRange: '{{startDate}} - {{endDate}}',
        contractUntil: 'Until {{endDate}}',
        contractFrom: 'From {{startDate}}',
        dialog: {
          openProfile: 'Open profile for {{memberName}}',
          jobTitle: 'Job title',
          phone: 'Phone',
          linkedInUrl: 'LinkedIn',
          totalExperience: 'Total experience',
          currentRoleExperience: 'Experience in current role',
          coreSkills: 'Core skills',
          previousExperiences: 'Previous experiences',
          upskillingGoals: 'Upskilling goals',
          activeContracts: 'Active contracts',
          noActiveContracts: 'No active contract is currently visible for this member.',
          openContract: 'Open contract'
        }
      },
      groupAnalysis: {
        title: 'Portfolio analysis',
        subtitle: 'Stored portfolio analysis history for {{groupName}}.',
        toolbarEyebrow: 'Group analysis',
        toolbarCopy: 'Review the latest agent-generated analysis for the selected group and browse previous snapshots stored in the CRM.',
        generate: 'Generate new analysis',
        generating: 'Work in progress',
        loading: 'Loading portfolio analysis...',
        emptyTitle: 'No stored portfolio analysis is available yet.',
        emptyDetail: 'Ask the agent to generate the group portfolio analysis, then refresh this view.',
        loadErrorTitle: 'Unable to load portfolio analysis.',
        historyTitle: 'History',
        historySubtitle: 'Previous snapshots in the current language.',
        emptyHistory: 'No previous analysis is stored yet.',
        sections: {
          analysisDate: 'Analysis date',
          portfolioOverview: 'Portfolio overview',
          sectorAnalysisAndTransformationSignals: 'Sector analysis & transformation signals',
          commercialMaturityEvaluation: 'Commercial maturity evaluation',
          priorityCommercialOpportunities: 'Priority commercial opportunities',
          portfolioGovernanceRecommendations: 'Portfolio governance recommendations',
          summary: 'Summary',
          immediateNextStep: 'Immediate next step (for you)'
        }
      }
    },
    companyDetail: {
      hero: {
        eyebrow: 'Company profile',
        loadingTitle: 'Loading company...',
        loadingCopy: 'Fetching the latest company details from the CRM.',
        errorTitle: 'Unable to load company',
        unavailableTitle: 'The company detail page is unavailable right now.',
        unavailableDetail: 'Return to the directory and try again.',
        deleteCompany: 'Delete company from CRM',
        deleting: 'Deleting...',
        deleteError: 'Unable to delete the company.',
        summaryAria: 'Company profile summary',
        missingDescription: 'No company description is available yet.',
        location: 'Location',
        mainContact: 'Main contact',
        statRevenue: 'Annual revenue',
        statOpenActions: 'Open actions',
        statActiveContracts: 'Active contracts',
        statTags: 'Account tags'
      },
      navigation: {
        ariaLabel: 'Company detail sections',
        overview: 'Overview',
        strategic: 'Strategic',
        customers: 'Customers',
        comments: 'Comments',
        actions: 'Actions',
        contracts: 'Contracts'
      },
      overview: {
        title: 'Overview',
        subtitle: 'Core company information and linked references.',
        businessSnapshot: 'Business snapshot',
        relationshipRhythm: 'Relationship rhythm',
        channels: 'Channels',
        industry: 'Industry',
        location: 'Location',
        employeeCount: 'Employee count',
        annualRevenue: 'Annual revenue',
        leadSource: 'Lead source',
        ownerUser: 'Account owner',
        saveOwnerUser: 'Save owner',
        ownerUpdateError: 'Unable to update the account owner.',
        ownerGroup: 'Owner group',
        mainContact: 'Main contact',
        parentCompany: 'Parent company',
        tags: 'Tags',
        tagsAria: 'Company tags',
        websiteAction: 'Open website',
        linkedInAction: 'Open LinkedIn'
      },
      communication: {
        title: 'Communication',
        subtitle: 'Company channels and relationship timeline.',
        website: 'Website',
        linkedIn: 'LinkedIn',
        notes: 'Notes'
      },
      strategic: {
        title: 'Strategic view',
        subtitle: 'Business signals and commercial context for this account.',
        emptyTitle: 'No strategic information is recorded for this company.',
        emptyDetail: 'Update the company strategy, recent news and commercial opportunities from the CRM tools.',
        coverage: 'Coverage',
        currentStrategy: 'Current strategy',
        recentNews: 'Recent news',
        commercialOpportunities: 'Commercial opportunities',
        recommendedNextActions: 'Recommended next actions',
        missingCurrentStrategy: 'No current strategy is recorded.',
        missingRecentNews: 'No recent news is recorded.',
        missingCommercialOpportunities: 'No commercial opportunities are recorded.',
        missingRecommendedNextActions: 'No recommended next actions are recorded.'
      },
      customers: {
        title: 'Customers',
        subtitle: 'People linked to this company account.',
        kindBadge: 'Contact',
        toolbarEyebrow: 'Relationship map',
        toolbarCopy: 'Search the account contacts and keep the selected profile open in a dedicated workspace.',
        filteredCopy: '{{count}} contacts match the current filters.',
        searchPlaceholder: 'Search by name, rôle, email or owner',
        filterAll: 'All contacts',
        filterMain: 'Main contact',
        filterAssigned: 'Assigned',
        filterUnassigned: 'Unassigned',
        actionFailed: 'Customer action failed.',
        newHeading: 'New customer',
        createLabel: 'Create customer',
        emptyTitle: 'No customers are attached to this company.',
        emptyDetail: 'Add customer records linked to this company to populate this section.',
        noResultsTitle: 'No contacts match the current filters.',
        noResultsDetail: 'Adjust the search terms or filter pills to widen the result set.',
        missingJobTitle: 'Job title unavailable',
        loadingEditor: 'Loading customer editor...',
        loadingEditorDetail: 'Fetching the latest customer details from the CRM.',
        detailHeading: 'Customer details',
        editHeading: 'Edit customer',
        workspaceTitle: 'Select a contact to review or edit it from the pinned workspace.',
        workspaceDetail: 'The selected contact stays open here while the list remains visible.',
        loadError: 'Unable to load customer details.',
        createError: 'Unable to create customer.',
        updateError: 'Unable to update customer.',
        deleteError: 'Unable to delete customer.',
        mainContactError: 'Unable to update the main contact.',
        noRelationshipOwner: 'No relationship owner',
        linkedInAction: 'Open LinkedIn'
      },
      contracts: {
        title: 'Contracts',
        subtitle: 'Commercial engagements tied to this company.',
        kindBadge: 'Contract',
        toolbarEyebrow: 'Contract portfolio',
        toolbarCopy: 'Review live, upcoming and completed engagements without leaving the company page.',
        filteredCopy: '{{count}} contracts match the current filters.',
        searchPlaceholder: 'Search by customer or offering',
        filterAll: 'All contracts',
        filterActive: 'Active',
        filterUpcoming: 'Upcoming',
        filterEnded: 'Ended',
        actionFailed: 'Contract action failed.',
        newHeading: 'New contract',
        createLabel: 'Create contract',
        emptyTitle: 'No contracts are attached to this company.',
        emptyDetail: 'Contracts linked in the CRM will appear here.',
        noResultsTitle: 'No contracts match the current filters.',
        noResultsDetail: 'Try a wider search or switch to another lifecycle filter.',
        dailyRate: 'Daily rate',
        duration: 'Duration',
        endDate: 'End date',
        customer: 'Customer',
        loadingEditor: 'Loading contract editor...',
        loadingEditorDetail: 'Fetching the latest contract details from the CRM.',
        detailHeading: 'Contract details',
        editHeading: 'Edit contract',
        workspaceTitle: 'Select a contract to inspect the commercial details side by side.',
        workspaceDetail: 'The contract workspace stays pinned while you browse the portfolio.',
        lifecycle: {
          active: 'Active',
          upcoming: 'Upcoming',
          ended: 'Ended'
        },
        loadError: 'Unable to load contract details.',
        createError: 'Unable to create contract.',
        updateError: 'Unable to update contract.',
        deleteError: 'Unable to delete contract.'
      },
      rex: {
        title: 'REX',
        subtitle: 'Structured experience feedback linked to contracts.',
        toolbarEyebrow: 'Experience feedback',
        toolbarCopy: 'Capture challenges, solutions and delivered value for completed or active engagements.',
        filteredCopy: '{{count}} REX match the current filters.',
        searchPlaceholder: 'Search by title, contract, consultant or content',
        addAction: 'Add REX',
        actionFailed: 'REX operation failed.',
        newHeading: 'New REX',
        detailHeading: 'REX details',
        editHeading: 'Edit REX',
        createLabel: 'Create REX',
        saveLabel: 'Save REX',
        emptyTitle: 'No REX is attached to this company yet.',
        emptyDetail: 'Add structured feedback from contract delivery to keep reusable experience in the CRM.',
        noContractsTitle: 'No contract is available for REX.',
        noContractsDetail: 'Create a contract before adding experience feedback.',
        noResultsTitle: 'No REX matches the current filters.',
        noResultsDetail: 'Broaden the search terms to show more experience feedback.',
        unknownContract: 'Unknown contract',
        untitled: 'Untitled REX',
        noConsultants: 'No consultant listed',
        emptySection: 'No content recorded.',
        loadingEditor: 'Loading REX editor...',
        loadingEditorDetail: 'Fetching the latest REX details from the CRM.',
        loadError: 'Unable to load REX details.',
        createError: 'Unable to create REX.',
        updateError: 'Unable to update REX.',
        deleteError: 'Unable to delete REX.',
        fields: {
          contract: 'Contract',
          title: 'Title',
          description: 'Description',
          challenges: 'CHALLENGES',
          solution: 'SOLUTION',
          resultsAndValueAdded: 'RESULTS AND VALUE ADDED'
        }
      },
      actions: {
        title: 'Actions',
        subtitle: 'Next steps, follow-ups and internal tasks tied to this account.',
        kindBadge: 'Action',
        toolbarEyebrow: 'Execution board',
        toolbarCopy: 'Track what is moving, blocked or overdue directly from the account view.',
        filteredCopy: '{{count}} actions match the current filters.',
        searchPlaceholder: 'Search by title, owner, customer or offering',
        filterAll: 'All actions',
        filterOpen: 'Open',
        filterOverdue: 'Overdue',
        filterDone: 'Done',
        actionFailed: 'Action operation failed.',
        newHeading: 'New action',
        createLabel: 'Create action',
        emptyTitle: 'No actions are attached to this company.',
        emptyDetail: 'Track follow-ups, meetings and internal work from this panel.',
        noResultsTitle: 'No actions match the current filters.',
        noResultsDetail: 'Broaden the search or change the status bucket.',
        missingTitle: 'Untitled action',
        noDueDate: 'No due date',
        loadingEditor: 'Loading action editor...',
        loadingEditorDetail: 'Fetching the latest action details from the CRM.',
        detailHeading: 'Action details',
        editHeading: 'Edit action',
        workspaceTitle: 'Select an action to review the exécution context and update it.',
        workspaceDetail: 'The action workspace stays visible while you move through the action list.',
        loadError: 'Unable to load action details.',
        createError: 'Unable to create action.',
        updateError: 'Unable to update action.',
        deleteError: 'Unable to delete action.'
      },
      comments: {
        title: 'Notes',
        subtitle: 'Typed relationship notes and supporting files captured over time for this account.',
        toolbarEyebrow: 'Account notes',
        toolbarCopy: 'Record interactions, reflections and opportunities directly from the company page.',
        filteredCopy: '{{count}} notes match the current filters.',
        searchPlaceholder: 'Search by content, type or author',
        filterAll: 'All notes',
        actionFailed: 'Note operation failed.',
        newHeading: 'New note',
        createLabel: 'Create note',
        emptyTitle: 'No notes are attached to this company.',
        emptyDetail: 'Add notes to build the relationship history and surface next commercial signals.',
        noResultsTitle: 'No notes match the current filters.',
        noResultsDetail: 'Adjust the search terms to widen the result set.',
        emptyEntry: 'No note content recorded.',
        loadingEditor: 'Loading note editor...',
        loadingEditorDetail: 'Fetching the latest note details from the CRM.',
        detailHeading: 'Note details',
        editHeading: 'Edit note',
        loadError: 'Unable to load note details.',
        createError: 'Unable to create note.',
        updateError: 'Unable to update note.',
        deleteError: 'Unable to delete note.',
        documentsUploadError: 'Unable to upload the note document.',
        documentsDownloadError: 'Unable to download the note document.',
        documentsDeleteError: 'Unable to delete the note document.'
      },
      documents: {
        title: 'Documents',
        subtitle: 'Files attached to this account and prepared for future LLM usage.',
        toolbarEyebrow: 'Document vault',
        toolbarCopy: '{{count}} document(s) are currently attached to this company.',
        toolbarEmpty: 'Upload account documents here so they can later be explored by the CRM tools and agents.',
        maxFileSize: 'Maximum upload size: 50 MB per file.',
        selectFile: 'Select a file',
        upload: 'Upload document',
        uploading: 'Uploading...',
        emptyTitle: 'No document is attached to this company yet.',
        emptyDetail: 'Upload PDFs, Word, PowerPoint, or Excel files to keep account context in the CRM.',
        errorTitle: 'Document operation failed.',
        download: 'Download',
        deleteConfirm: 'Delete {{fileName}} from this company?',
        status: {
          pending: 'Extraction pending',
          completed: 'Text extracted',
          failed: 'Extraction failed'
        },
        webLinks: {
          eyebrow: 'External links',
          copy: 'Reference useful websites or documents that live outside the CRM.',
          label: 'Link label',
          url: 'URL',
          urlPlaceholder: 'https://example.com/resource',
          description: 'Description',
          addAction: 'Add link',
          empty: 'No external web link is attached to this company yet.',
          unnamed: 'External link',
          deleteConfirm: 'Remove {{label}} from this company?'
        }
      }
    },
    commentDetail: {
      eyebrow: 'Note editor',
      headings: {
        details: 'Note details'
      },
      sections: {
        entry: 'Note entry',
        entryCopy: 'Capture the interaction, reflection or opportunity worth keeping in the account history.'
      },
      fields: {
        type: 'Type',
        customer: 'Linked customer',
        contract: 'Linked contract',
        author: 'Author',
        createdAt: 'Created at',
        updatedAt: 'Updated at',
        updatedBy: 'Updated by',
        content: 'Note'
      },
      placeholders: {
        content: 'Write the interaction summary, reflection, opportunity or relationship context to keep in the company history.'
      },
      hints: {
        edited: 'This note was updated after it was first created.'
      },
      empty: {
        content: 'No note content recorded.'
      },
      customer: {
        companyWide: 'Company-wide note'
      },
      contract: {
        none: 'No linked contract'
      },
      documents: {
        eyebrow: 'Attached documents',
        createHint: 'Save the note first, then attach supporting documents.',
        editCopy: 'Attach supporting files directly to this note.',
        readOnlyCopy: 'Download the supporting files attached to this note.',
        selectFile: 'Select a file',
        upload: 'Upload document',
        uploading: 'Uploading...',
        empty: 'No document is attached to this note yet.',
        deleteConfirm: 'Delete {{fileName}} from this note?'
      }
    },
    actionDetail: {
      eyebrow: 'Action editor',
      headings: {
        details: 'Action details'
      },
      sections: {
        core: 'Core setup',
        coreCopy: 'Define what needs to happen and who owns it.',
        relationships: 'Commercial links',
        relationshipsCopy: 'Link the action to the right customer, contract and offering.',
        planning: 'Timeline',
        planningCopy: 'Capture the expected timing for delivery and completion.',
        context: 'Context and outcome',
        contextCopy: 'Document the brief, the expected result and the actual outcome.'
      },
      fields: {
        title: 'Title',
        description: 'Description',
        type: 'Type',
        status: 'Status',
        priority: 'Priority',
        dueDate: 'Due date',
        dueTime: 'Due time',
        scheduledAt: 'Scheduled at',
        scheduledTime: 'Scheduled time',
        completedAt: 'Completed at',
        completedTime: 'Completed time',
        ownerUserId: 'Owner user',
        customer: 'Customer',
        contract: 'Contract',
        offering: 'Offering',
        outcome: 'Outcome',
        reminderEnabled: 'Reminder enabled'
      },
      placeholders: {
        title: 'Follow up after proposal review',
        description: 'Describe the expected next step and the context for the team.',
        outcome: 'Capture the result once the action is completed.'
      },
      hints: {
        lockedByContract: 'Customer and offering are locked because they are inherited from the selected contract.'
      },
      empty: {
        description: 'No description recorded.',
        outcome: 'No outcome recorded.'
      }
    },
    customerDetail: {
      eyebrow: 'Customer editor',
      headings: {
        details: 'Customer details'
      },
      sections: {
        identity: 'Identity and channels',
        identityCopy: 'Capture the primary identity and direct contact paths.',
        relationship: 'Relationship ownership',
        relationshipCopy: 'Clarify how mature the relationship is and who manages it.',
        context: 'Context notes',
        contextCopy: 'Document motivations, risks and relationship context for the team.'
      },
      fields: {
        firstName: 'First name',
        lastName: 'Last name',
        jobTitle: 'Job title',
        phoneNumber: 'Phone number',
        email: 'Email',
        linkedInUrl: 'LinkedIn URL',
        relationshipStatus: 'Relationship status',
        inRelationshipWith: 'In relationship with',
        keyInterests: 'Key interests',
        keyConcerns: 'Key concerns',
        comments: 'Comments'
      },
      placeholders: {
        firstName: 'Morgan',
        lastName: 'Lee',
        jobTitle: 'Head of Partnerships',
        phoneNumber: '+33 6 12 34 56 78',
        email: 'morgan.lee@example.com',
        linkedInUrl: 'https://www.linkedin.com/in/example',
        keyInterests: 'Topics, products and priorities that matter to this customer.',
        keyConcerns: 'Risks, blockers and objections to keep in mind.',
        comments: 'Relationship notes, context and recent interactions.'
      },
      empty: {
        keyInterests: 'No key interests recorded.',
        keyConcerns: 'No key concerns recorded.',
        comments: 'No comments recorded.'
      },
      linkedNotes: {
        title: 'Linked notes',
        copy: 'Review the CRM notes explicitly attached to this customer.',
        empty: 'No note is linked to this customer yet.',
        openAction: 'View note',
        editAction: 'Edit note'
      }
    },
    offeringDetail: {
      eyebrow: 'Offering editor',
      headings: {
        new: 'New offering',
        details: 'Offering details'
      },
      fields: {
        name: 'Name',
        offeringType: 'Offering type',
        description: 'Description'
      },
      placeholders: {
        name: 'Discovery workshop',
        description: 'Explain what this offering delivers and when it should be proposed.'
      },
      actions: {
        create: 'Create offering',
        save: 'Save offering'
      },
      documents: {
        eyebrow: 'Offering documents',
        title: 'Attached documents',
        toolbarCopy: '{{count}} document(s) are currently attached to this offering.',
        toolbarEmpty: 'Upload offering collateral here so it stays available in the CRM document flow.',
        maxFileSize: 'Maximum upload size: 50 MB per file.',
        selectFile: 'Select a file',
        upload: 'Upload document',
        uploading: 'Uploading...',
        loading: 'Loading documents...',
        emptyTitle: 'No document is attached to this offering yet.',
        emptyDetail: 'Upload PDFs, Word, PowerPoint, or Excel files linked to this offering.',
        errorTitle: 'Document operation failed.',
        download: 'Download',
        deleteConfirm: 'Delete {{fileName}} from this offering?',
        status: {
          pending: 'Extraction pending',
          completed: 'Text extracted',
          failed: 'Extraction failed'
        }
      },
      empty: {
        description: 'No offering description recorded.'
      }
    },
    contractDetail: {
      eyebrow: 'Contract editor',
      headings: {
        details: 'Contract details'
      },
      sections: {
        scope: 'Scope',
        scopeCopy: 'Link the engagement to the right customer and offering.',
        timeline: 'Timeline',
        timelineCopy: 'Keep dates and duration aligned with the actual engagement.',
        commercials: 'Commercials',
        commercialsCopy: 'Track headline value and daily economics.',
        staffing: 'Staffing',
        staffingCopy: 'Assign one or more consultants from the group or register named external consultants.'
      },
      fields: {
        customer: 'Customer',
        offering: 'Offering',
        startDate: 'Start date',
        endDate: 'End date',
        durationInDays: 'Duration in days',
        totalInvoice: 'Total invoice',
        dailyRate: 'Daily rate',
        consultants: 'Consultants',
        groupConsultant: 'Group consultant',
        externalConsultant: 'External consultant'
      },
      actions: {
        addGroupConsultant: 'Add group consultant',
        addExternalConsultant: 'Add external consultant'
      },
      consultants: {
        groupMember: 'Group member',
        external: 'External',
        selectGroupMember: 'Select a group member',
        empty: 'No consultant is assigned to this contract yet.'
      },
      lifecycle: {
        active: 'Active',
        upcoming: 'Upcoming',
        ended: 'Ended'
      },
      linkedNotes: {
        title: 'Linked notes',
        copy: 'Review the CRM notes explicitly attached to this contract.',
        empty: 'No note is linked to this contract yet.',
        openAction: 'View note',
        editAction: 'Edit note'
      }
    },
    admin: {
      hero: {
        eyebrow: 'Administration',
        title: 'User group management',
        copy: 'Review the groups you can access, create new ones from the ADMIN group, and manage members where you are an admin.',
        summaryAria: 'Admin page summary'
      },
      access: {
        title: 'Admin access is not available',
        subtitle: 'You need to administer at least one group to use this space.',
        detail: 'Join the ADMIN group to create new groups, or become an admin of an existing group to manage its members.'
      },
      create: {
        title: 'Create a group',
        subtitle: 'Provision a new group for CRM permissions and collaboration.',
        groupName: 'Group name',
        placeholder: 'Example: Sales Operations',
        currentDirectory: 'Current directory',
        adminCoverage: 'Admin coverage',
        manageableGroups: 'Manageable groups',
        restricted: 'Only members of the ADMIN group can create new groups.',
        creating: 'Creating...'
      },
      delete: {
        title: 'Delete a group',
        subtitle: 'Remove an unused group when you have ADMIN system access.',
        group: 'Group to delete',
        hint: 'The ADMIN system group cannot be deleted. Groups that still own companies are blocked on the backend.',
        empty: 'No deletable group is currently available.',
        action: 'Delete group',
        deleting: 'Deleting...',
        confirm: 'Delete the group "{{groupName}}"? This cannot be undone.'
      },
      membership: {
        title: 'Add a user to a group',
        subtitle: 'Add a member or an admin to one of the groups you manage.',
        editTitle: 'Edit a visible user',
        editSubtitle: 'Update the selected user profile and rôle in {{groupName}}.',
        empty: 'You are not an admin of any group yet.',
        group: 'Target group',
        userId: 'OAuth user id',
        userIdPlaceholder: 'provider|user-id',
        role: 'Rôle',
        memberRole: 'Member',
        adminRole: 'Admin',
        firstName: 'First name',
        lastName: 'Last name',
        email: 'Email',
        phone: 'Phone',
        linkedInUrl: 'LinkedIn URL',
        linkedInUrlPlaceholder: 'https://www.linkedin.com/in/example',
        experienceStartDate: 'Experience start date',
        jobTitle: 'Job title',
        currentRoleStartDate: 'Current role start date',
        coreSkills: 'Core skills',
        previousExperiences: 'Previous experiences',
        upskillingGoals: 'Upskilling goals',
        skillTagsPreview: 'Skill tags preview',
        skillTagsHint: 'Use commas, semicolons or line breaks in the skills field to manage tags.',
        dialogHint: 'Use the dedicated dialog to add a new user, then use Edit from the directory cards to update an existing profile.',
        addAction: 'Add user',
        saveAction: 'Save user',
        cancelEdit: 'Cancel edit',
        editLockedHint: 'Group and OAuth user id stay locked while editing an existing user.',
        saving: 'Saving...'
      },
      directory: {
        title: 'Managed groups',
        subtitle: 'Groups where you currently hold admin rights.',
        loading: 'Loading groups...',
        emptyTitle: 'No managed groups are available yet.',
        emptyDetail: 'Create a group from ADMIN access or ask to be added as an admin to an existing group.',
        adminGroup: 'Admin group',
        members: 'Members',
        admins: 'Admins',
        fileStorage: 'Storage: {{size}}',
        documentsAction: 'Files',
        deleteAction: 'Delete user',
        noAdmins: 'No admins are registered in this group.',
        noMembers: 'No members are registered in this group.',
        documentsDialog: {
          title: 'Discussion files for {{userName}}',
          loading: 'Loading discussion files...',
          empty: 'No discussion file is currently stored for this user.',
          deleteConfirm: 'Delete "{{fileName}}" from this user discussion history?',
          untitledConversation: 'Discussion {{shortId}}'
        },
        deleteDialog: {
          title: 'Delete {{userName}}',
          loading: 'Loading deletion impact...',
          intro: 'This removes the CRM profile, all group memberships, stored discussions, and attached files for this user.',
          summary: '{{groups}} group memberships, {{conversations}} discussions, and {{documents}} documents will be deleted.',
          confirmCopy: 'Delete this user profile and all listed content? This cannot be undone.',
          blockedTitle: 'Deletion is currently blocked.',
          blockedSelf: 'You cannot delete your own user profile.',
          blockedMissingAccess: 'This user still belongs to groups you do not administer.',
          blockedLastAdmin: '"{{groupName}}" would be left without an admin.',
          groupsTitle: 'Group memberships',
          groupsEmpty: 'No group membership is currently recorded for this user.',
          conversationsTitle: 'Discussions',
          conversationsEmpty: 'No stored discussion will be deleted.',
          conversationMeta: '{{messages}} messages · {{documents}} documents',
          standaloneDocumentsTitle: 'Standalone files',
          standaloneDocumentsEmpty: 'No standalone file will be deleted.',
          untitledConversation: 'Discussion {{shortId}}',
          groupRoleAdmin: 'Admin',
          groupRoleMember: 'Member',
          groupAdminGroup: 'System group',
          groupNotManaged: 'Not administered by you',
          groupLastAdmin: 'Last admin',
          close: 'Cancel',
          action: 'Delete user',
          deleting: 'Deleting...'
        }
      },
      databaseMaintenance: {
        title: 'Database maintenance',
        subtitle: 'Daily backups are stored on the server. Only ADMIN group members can trigger a backup or restore the database.',
        backupsTitle: 'Available backups',
        backupsCopy: 'Recent backup files stored by the server.',
        loading: 'Loading backups...',
        empty: 'No backup file is available yet.',
        downloadAction: 'Download',
        createBackup: 'Create backup now',
        creatingBackup: 'Creating backup...',
        restoreTitle: 'Restore database',
        restoreCopy: 'Upload a backup file to replace the current database. A safety backup is created automatically before restore.',
        restoreConfirm: 'Restore the full database from the selected backup file? The current data will be replaced.',
        restoreFileLabel: 'Backup file',
        restoreSelectedFile: 'Selected file: {{fileName}}',
        restoreHint: 'Accepted files: .gz or .json.gz generated by the CRM backup service.',
        restoreAction: 'Restore database',
        restoring: 'Restoring...',
        restoreSuccess: 'Restore completed from {{fileName}}.',
        restoreSummary: '{{collections}} collections and {{documents}} documents were restored.',
        preRestoreBackup: 'Safety backup created before restore: {{fileName}}',
        lastBackupCreated: 'Backup created: {{fileName}}',
        triggers: {
          manual: 'Manual',
          scheduled: 'Scheduled',
          pre_restore: 'Pre-restore'
        }
      }
    },
    userDetail: {
      hero: {
        eyebrow: 'CRM user',
        loadingTitle: 'Loading user profile',
        loadingCopy: 'The CRM profile is being retrieved.',
        errorTitle: 'User profile unavailable',
        unavailableTitle: 'User profile unavailable',
        unavailableDetail: 'This user is not available from your current CRM visibility.'
      }
    },
    profile: {
      title: 'Profile',
      hero: {
        eyebrow: 'Personal profile',
        title: 'Your profile',
        copy: 'Update the personal information attached to your OAuth account id.',
        skillTags: 'Skill tags'
      },
      form: {
        title: 'Profile details',
        subtitle: 'These fields are stored in the CRM and reused in administration screens.',
        loading: 'Loading your profile...',
        firstName: 'First name',
        lastName: 'Last name',
        email: 'Email',
        phone: 'Phone',
        linkedInUrl: 'LinkedIn URL',
        linkedInUrlPlaceholder: 'https://www.linkedin.com/in/example',
        experienceStartDate: 'Experience start date',
        jobTitle: 'Job title',
        currentRoleStartDate: 'Current role start date',
        coreSkills: 'Core skills',
        previousExperiences: 'Previous experiences',
        upskillingGoals: 'Upskilling goals',
        tags: 'Skill tags',
        tagsHint: 'Add or remove the tags that best describe your current skills profile.',
        tagsAddLabel: 'Add a tag',
        tagsPlaceholder: 'Example: sales',
        tagsAddAction: 'Add tag',
        tagsEmpty: 'No skill tag is currently defined.',
        skillTagsPreview: 'Skill tags preview',
        skillTagsHint: 'Use commas, semicolons or line breaks in the skills field to manage tags.',
        language: 'Language',
        theme: 'Theme',
        save: 'Save profile',
        saveSuccess: 'Profile saved.',
        saving: 'Saving...'
      },
        documents: {
          title: 'Published documents',
          subtitle: 'All documents you uploaded for your chat conversations.',
          loading: 'Loading your documents...',
          empty: 'No document has been uploaded yet.',
          deleteConfirm: 'Delete "{{fileName}}" from your discussion files?',
          downloadAction: 'Download',
          downloading: 'Downloading...'
        },
        conversations: {
          title: 'Discussions',
          subtitle: 'Browse your recent chat discussions, review their messages, and remove the ones you no longer need.',
          loading: 'Loading your discussions...',
          empty: 'No discussion has been stored yet.',
          untitled: 'Discussion {{shortId}}',
          noDocuments: 'No file is attached to this discussion.',
          deleteConfirm: 'Delete "{{conversationName}}" and its attached files?',
          dialog: {
            empty: 'No message has been stored for this discussion yet.',
            roles: {
              assistant: 'Assistant',
              user: 'User',
              tool: 'Tool',
              system: 'System'
            }
          }
        }
      },
    chat: {
      title: 'Clippy',
      titleExpanded: 'Clippy',
      collapse: 'Collapse chat window',
      expand: 'Expand chat window',
      close: 'Close chat',
      open: 'Open assistant chat',
      helpAction: 'Ask what I can help with',
      conversations: {
        open: 'Open your discussions',
        empty: 'No discussion yet',
        untitled: 'Discussion'
      },
      helpPrompt: 'What can I ask to you?',
      suggestions: {
        weeklyPriorities: 'Summarize my top priorities for this week.',
        accountsNeedingAttention: 'Which accounts need my attention first?',
        followUpEmailDraft: 'Draft a follow-up email after a first meeting.'
      },
      composer: {
        message: 'Message',
        placeholder: 'Ask your question.',
        footer: 'UI only for now'
      },
      documents: {
        eyebrow: 'Conversation files',
        title: 'Attached documents',
        addAction: 'Add files',
        uploading: 'Uploading...',
        loading: 'Loading conversation documents...',
        empty: 'No file has been attached to this conversation yet.',
        downloadAction: 'Download',
        downloading: 'Downloading...',
        deleteAction: 'Delete',
        deleteConfirm: 'Delete "{{fileName}}" from this conversation?'
      },
      notifications: {
        noSelectedServer: 'No selected LLM server',
        noAgentProfile: 'No LLM agent profile'
      }
    },
    allComponents: {
      hero: {
        eyebrow: 'Global Material theme',
        title: 'Dark palette, green accent, technical surfaces.',
        copy: 'All Material components now rely on the variables from `styles.scss` and a consistent monospace typography aligned with the current product style.',
        summaryAria: 'Theme highlights'
      },
      highlights: {
        dark: 'Dark',
        monospace: 'Monospace',
        dense: 'Dense',
        highContrast: 'High contrast'
      },
      sampleNotes: 'Dense CRM pipeline prepared for day-to-day usage.',
      tabs: {
        form: 'Form',
        actions: 'Actions'
      },
      form: {
        title: 'Primary contact',
        subtitle: 'Review fields, buttons and selectors',
        company: 'Company',
        status: 'Status',
        priority: 'Priority',
        notes: 'Notes',
        hint: 'Supporting text and focus state also use the custom theme.',
        nda: 'Signed NDA',
        notifications: 'Pipeline alerts',
        preferredChannel: 'Preferred channel',
        cancel: 'Cancel',
        saveDraft: 'Save draft',
        validate: 'Validate'
      },
      state: {
        title: 'Current state',
        subtitle: 'Quick read of the Material tokens',
        notifications: 'Notifications',
        channel: 'Channel'
      },
      actions: {
        title: 'Button variants',
        text: 'Text',
        outline: 'Outline',
        primary: 'Primary',
        surface: 'Surface'
      }
    },
    errors: {
      groups: {
        load: 'Unable to load groups.',
        required: 'Group name is required.',
        selectionRequired: 'A selected group is required.',
        create: 'Unable to create group.',
        delete: 'Unable to delete the selected group.',
        addUser: 'Unable to add the user to the selected group.',
        missingPayload: 'Group was created without payload.'
      },
      companies: {
        load: 'Unable to load companies.',
        detail: 'Unable to load company details.',
        documentsTooLarge: 'The selected company document exceeds the 50 MB limit.',
        documentsUpload: 'Unable to upload the selected company document.',
        documentsDownload: 'Unable to download the selected company document.',
        documentsDelete: 'Unable to delete the selected company document.',
        webLinksSave: 'Unable to save the company web links.'
      },
      offerings: {
        load: 'Unable to load offerings.',
        documentsTooLarge: 'The selected offering document exceeds the 50 MB limit.',
        documentsLoad: 'Unable to load the selected offering documents.',
        documentsUpload: 'Unable to upload the selected offering document.',
        documentsDownload: 'Unable to download the selected offering document.',
        documentsDelete: 'Unable to delete the selected offering document.'
      },
      groupPortfolioAnalysis: {
        load: 'Unable to load the group portfolio analysis.',
        history: 'Unable to load the group portfolio analysis history.',
        generate: 'Unable to generate a new group portfolio analysis.'
      },
      admin: {
        documentsLoad: 'Unable to load the selected user discussion files.',
        documentsDelete: 'Unable to delete the selected user discussion file.',
        deletionPreviewLoad: 'Unable to load the selected user deletion preview.',
        deleteUser: 'Unable to delete the selected user profile.'
      },
      profile: {
        load: 'Unable to load your profile.',
        save: 'Unable to save your profile.',
        documentsLoad: 'Unable to load your published documents.',
        documentsDownload: 'Unable to download the selected discussion document.',
        documentsDelete: 'Unable to delete the selected discussion document.',
        conversationsLoad: 'Unable to load your discussions.',
        conversationsOpen: 'Unable to open the selected discussion.',
        conversationsDelete: 'Unable to delete the selected discussion.'
      },
      chat: {
        documentsTooLarge: 'One of the selected files exceeds the 50 MB limit.',
        documentsUpload: 'Unable to upload the selected conversation files.',
        documentsDownload: 'Unable to download the selected conversation file.',
        documentsDelete: 'Unable to delete the selected conversation file.'
      },
      databaseAdmin: {
        loadBackups: 'Unable to load database backups.',
        createBackup: 'Unable to create a database backup.',
        downloadBackup: 'Unable to download the selected database backup.',
        restoreBackup: 'Unable to restore the database from the uploaded backup.'
      }
    }
  },
  fr: {
    app: {
      title: 'CRM Agentique',
      unauthenticated: {
        eyebrow: 'Transformation metier',
        prompt: 'Veuillez vous connecter avant de continuer.'
      },
      sessionExpired: {
        eyebrow: 'Session expirée',
        title: 'Vous n’etes plus connecté',
        prompt: 'Reconnectez-vous avec l’un des boutons de connexion ci-dessous. Les brouillons locaux restent disponibles sur cet appareil.'
      }
    },
    common: {
      languages: {
        english: 'Anglais',
        french: 'Francais',
        italian: 'Italien'
      },
      themes: {
        light: 'Clair',
        dark: 'Sombre',
        ninon: 'Ninon'
      },
      labels: {
        assistant: 'Assistant',
        unavailable: 'Indisponible',
        unknown: 'Inconnu',
        notSpecified: 'Non renseigne',
        notTracked: 'Non suivi',
        mainContact: 'Contact principal',
        selected: 'Selectionne',
        selectedGroup: 'Groupe sélectionné',
        allGroups: 'Tous les groupes',
        noGroupAssigned: 'Aucun groupe assigne',
        noMainContact: 'Aucun contact principal',
        unnamedCompany: 'Entreprise sans nom',
        unnamedContact: 'Contact sans nom',
        unnamedOffering: 'Offre sans nom',
        locationUnavailable: 'Localisation indisponible',
        revenueUnavailable: 'Chiffre d\'affaires indisponible',
        websiteUnavailable: 'Site web indisponible',
        phoneUnavailable: 'Téléphone indisponible',
        linkedInUnavailable: 'LinkedIn indisponible',
        unknownStatus: 'Statut inconnu',
        unknownSource: 'Source inconnue',
        unknownRelationship: 'Relation inconnue',
        unknownType: 'Type inconnu',
        unknownCompany: 'Entreprise inconnue',
        unknownContact: 'Contact inconnu',
        openEnded: 'Sans date de fin',
        startDateUnavailable: 'Date de début indisponible',
        noEmailAddress: 'Aucune adresse e-mail',
        noActionScheduled: 'Aucune action planifiée',
        active: 'Actives',
        disabled: 'Désactivées',
        available: 'Disponible',
        missing: 'Manquante',
        draftRestored: 'Brouillon local restauré.',
        requiredField: '{{label}} *',
        withCount: '{{label}} ({{count}})'
      },
      actions: {
        addAction: 'Ajouter une action',
        addComment: 'Ajouter un commentaire',
        addNote: 'Ajouter une note',
        addCustomer: 'Ajouter un client',
        addContract: 'Ajouter un contrat',
        addOffering: 'Ajouter une offre',
        admin: 'Admin',
        backToCompanies: 'Retour aux entreprises',
        backToHome: 'Retour a l accueil',
        cancel: 'Annuler',
        close: 'Fermer',
        createGroup: 'Créer un groupe',
        delete: 'Supprimer',
        detail: 'Détail',
        edit: 'Modifier',
        logout: 'Se déconnecter',
        refresh: 'Actualiser',
        refreshOfferings: 'Actualiser les offres',
        saveAction: 'Enregistrer l\'action',
        saveComment: 'Enregistrer la note',
        saveContract: 'Enregistrer le contrat',
        saveCustomer: 'Enregistrer le client',
        send: 'Envoyer',
        setAsMainContact: 'Définir comme contact principal',
        saving: 'Enregistrement...',
        viewDetails: 'Voir le détail'
      },
      confirm: {
        deleteAction: 'Supprimer cette action de l\'entreprise ?',
        deleteComment: 'Supprimer ce commentaire de l\'entreprise ?',
        deleteNote: 'Supprimer cette note de l\'entreprise ?',
        deleteRex: 'Supprimer ce REX de l\'entreprise ?',
        deleteCustomer: 'Supprimer ce client de l\'entreprise ?',
        deleteContract: 'Supprimer ce contrat de l\'entreprise ?',
        deleteOffering: 'Supprimer cette offre du catalogue ?',
        deleteCompany: 'Supprimer "{{companyName}}" du CRM ? Tous les clients, contrats, actions, notes et documents liés seront supprimés.'
      },
      counts: {
        companies: {
          one: '{{count}} entreprise',
          other: '{{count}} entreprises'
        },
        customers: {
          one: '{{count}} client',
          other: '{{count}} clients'
        },
        contacts: {
          one: '{{count}} contact',
          other: '{{count}} contacts'
        },
        activeCustomers: {
          one: '{{count}} client actif',
          other: '{{count}} clients actifs'
        },
        offerings: {
          one: '{{count}} offre',
          other: '{{count}} offres'
        },
        qualifiedCompanies: {
          one: '{{count}} entreprise qualifiée',
          other: '{{count}} entreprises qualifiées'
        },
        contracts: {
          one: '{{count}} contrat',
          other: '{{count}} contrats'
        },
        activeContracts: {
          one: '{{count}} contrat actif',
          other: '{{count}} contrats actifs'
        },
        openActions: {
          one: '{{count}} action ouverte',
          other: '{{count}} actions ouvertes'
        },
        actions: {
          one: '{{count}} action',
          other: '{{count}} actions'
        },
        comments: {
          one: '{{count}} commentaire',
          other: '{{count}} commentaires'
        },
        notes: {
          one: '{{count}} note',
          other: '{{count}} notes'
        },
        messages: {
          one: '{{count}} message',
          other: '{{count}} messages'
        },
        years: {
          one: '{{count}} an',
          other: '{{count}} ans'
        },
        documents: {
          one: '{{count}} document',
          other: '{{count}} documents'
        },
        webLinks: {
          one: '{{count}} lien web',
          other: '{{count}} liens web'
        },
        rex: {
          one: '{{count}} REX',
          other: '{{count}} REX'
        },
        groups: {
          one: '{{count}} groupe',
          other: '{{count}} groupes'
        },
        members: {
          one: '{{count}} membre',
          other: '{{count}} membres'
        },
        admins: {
          one: '{{count}} admin',
          other: '{{count}} admins'
        },
        adminSlots: {
          one: '{{count}} poste admin',
          other: '{{count}} postes admin'
        },
        days: {
          one: '{{count}} jour',
          other: '{{count}} jours'
        }
      },
      enums: {
        companyStatus: {
          PROSPECT: 'Prospect',
          QUALIFIED: 'Qualifiée',
          ACTIVE_CUSTOMER: 'Client actif',
          PARTNER: 'Partenaire',
          LOST: 'Perdue'
        },
        leadSource: {
          WEBSITE: 'Site web',
          REFERRAL: 'Recommandation',
          OUTBOUND: 'Prospection',
          EVENT: 'Événement',
          LINKEDIN: 'LinkedIn',
          OTHER: 'Autre'
        },
        relationshipStatus: {
          PROSPECT: 'Prospect',
          CUSTOMER: 'Client',
          OLD_RELATIONSHIP: 'Ancienne relation'
        },
        offeringType: {
          FIXED_PRICE: 'Forfait',
          TIME_AND_MATERIAL: 'Temps et matériel'
        },
        noteType: {
          COMMENT: 'Commentaire',
          REFLECTION: 'Réflexion',
          OPPORTUNITY: 'Opportunité à creuser'
        },
        actionType: {
          CALL: 'Appel',
          EMAIL: 'E-mail',
          MEETING: 'Réunion',
          FOLLOW_UP: 'Suivi',
          TODO: 'Tâche',
          LINKEDIN_OUTREACH: 'Prise de contact LinkedIn',
          PREPARE_PROPOSAL: 'Préparation de proposition',
          SEND_PROPOSAL: 'Envoi de proposition',
          NEGOTIATE_CONTRACT: 'Négociation de contrat',
          RENEWAL_CHECK: 'Point de renouvellement',
          UPSELL_CHECK: 'Point d\'upsell',
          INTERNAL_TASK: 'Tâche interne'
        },
        actionStatus: {
          OPEN: 'Ouverte',
          IN_PROGRESS: 'En cours',
          DONE: 'Terminée',
          CANCELLED: 'Annulée',
          BLOCKED: 'Bloquée',
          SNOOZED: 'Reportée',
          OVERDUE: 'En retard'
        },
        actionPriority: {
          LOW: 'Basse',
          NORMAL: 'Normale',
          HIGH: 'Haute',
          URGENT: 'Urgente'
        },
        previewStatus: {
          NEW: 'Nouveau',
          QUALIFIED: 'Qualifié',
          FOLLOW_UP: 'En suivi',
          CONVERTED: 'Converti'
        },
        previewPriority: {
          LOW: 'Basse',
          NORMAL: 'Normale',
          HIGH: 'Haute'
        },
        previewChannel: {
          email: 'E-mail',
          phone: 'Téléphone',
          meet: 'Visio'
        }
      }
    },
    mainMenu: {
      home: 'Accueil',
      loadingGroups: 'Chargement des groupes...',
      noGroupsAvailable: 'Aucun groupe disponible',
      signInProviders: 'Fournisseurs de connexion',
      signInWithGoogle: 'Se connecter avec Google',
      signInWithMicrosoft: 'Se connecter avec Microsoft',
      styleGuide: 'Guide de style',
      languageSelector: 'Sélecteur de langue',
      themeSelector: 'Sélecteur de thème'
    },
    home: {
      tabs: {
        dashboard: 'Tableau de bord',
        groupPortfolio: 'Portefeuille de clients',
        groupMembers: 'Membres du groupe',
        groupAnalysis: 'Analyse portefeuille',
        offerings: 'Offres'
      },
      hero: {
        eyebrow: 'Cockpit commercial',
        title: 'Concentrez-vous sur les comptes qui demandent une action maintenant',
        copy: 'Commencez par la charge active, gardez un œil sur les opportunités qualifiées et ouvrez directement depuis l\'accueil le compte qui demande de l\'attention.',
        summaryAria: 'Résumé du portefeuille'
      },
      focus: {
        title: 'Comptes à traiter',
        subtitle: 'Une courte liste de comptes classés selon le travail ouvert, les contrats en cours et le potentiel commercial.',
        loading: 'Chargement des comptes prioritaires...',
        emptyTitle: 'Aucun compte ne demande d\'attention pour le moment.',
        emptyDetail: 'Créez des entreprises ou changez de groupe pour alimenter cette liste de priorité.',
        openCompanyAria: 'Ouvrir {{companyName}}',
        summaryAria: 'Résumé des comptes prioritaires',
        priorityOpenActions: 'Suivi en cours',
        priorityActiveContracts: 'Compte actif à protéger',
        priorityQualified: 'Opportunité qualifiée à pousser',
        priorityCoverage: 'Couverture à construire'
      },
      actionTimeline: {
        title: 'Chronologie des actions',
        subtitle: 'Vos actions ouvertes qui demandent de l\'attention maintenant, groupées par horizon d\'exécution.',
        loading: 'Chargement de la chronologie des actions...',
        emptyTitle: 'Aucune action urgente n\'est visible pour le moment.',
        emptyDetail: 'Vos actions affectées avec une date en retard, aujourd\'hui ou cette semaine apparaîtront ici.',
        emptyBucket: 'Aucune action sur cet horizon.',
        overdue: 'En retard',
        today: 'Aujourd\'hui',
        thisWeek: 'Cette semaine',
        dueLabel: 'Échéance {{date}}',
        scheduledLabel: 'Planifiée {{date}}',
        noDate: 'Aucune date',
        openAccount: 'Ouvrir le compte'
      },
      opportunities: {
        title: 'Notes opportunités',
        subtitle: 'Opportunités commerciales récentes capturées sur le portefeuille de comptes visible.',
        loading: 'Chargement des notes opportunités...',
        emptyTitle: 'Aucune note opportunité n\'est visible pour le moment.',
        emptyDetail: 'Créez des notes de type opportunité depuis les fiches compte pour les faire remonter ici.',
        companyWide: 'Note au niveau entreprise',
        openOpportunityAria: 'Ouvrir les notes opportunités de {{companyName}}'
      },
      offerings: {
        title: 'Catalogue des offres',
        subtitle: 'Offres commerciales indépendantes disponibles dans le CRM.',
        toolbarEyebrow: 'Catalogue commercial',
        toolbarCopy: 'Consultez le catalogue d\'offres actuel puis ouvrez une offre dans une dialog pour la créer, la modifier ou gérer ses fichiers joints.',
        newHeading: 'Nouvelle offre',
        createLabel: 'Créer l\'offre',
        detailHeading: 'Détail de l\'offre',
        editHeading: 'Modifier l\'offre',
        loading: 'Chargement des offres...',
        loadingEditor: 'Chargement de la dialog d\'offre...',
        loadingEditorDetail: 'Récupération des derniers détails de l\'offre depuis le CRM.',
        emptyTitle: 'Aucune offre n\'est encore disponible.',
        emptyDetail: 'Créez une offre depuis ce catalogue puis rouvrez-la pour joindre des documents.',
        missingDescription: 'Aucune description d\'offre n\'est encore disponible.',
        workspaceTitle: 'Sélectionnez une offre pour la consulter ou la modifier.',
        workspaceDetail: 'L\'offre sélectionnée reste ouverte ici pendant que le catalogue reste visible.',
        actionFailed: 'L\'action sur l\'offre a échoué.',
        loadError: 'Impossible de charger le détail de l\'offre.',
        createError: 'Impossible de créer l\'offre.',
        updateError: 'Impossible de mettre à jour l\'offre.',
        deleteError: 'Impossible de supprimer l\'offre.',
        refreshing: 'Actualisation...'
      },
      companies: {
        title: 'Portefeuille de clients',
        subtitle: 'Comptes clients visibles avec recherche, filtres par statut et tags, et tri des signaux commerciaux.',
        toolbarEyebrow: 'Portefeuille clients',
        toolbarCopy: 'Recherchez dans le portefeuille visible, combinez les filtres par statut et tags, puis classez les comptes selon la charge active, la couverture, le nom ou le revenu contrat.',
        filteredCopy: '{{count}} compte(s) client correspondent aux filtres actuels.',
        loading: 'Chargement des entreprises...',
        emptyTitle: 'Aucune entreprise n\'est encore disponible.',
        emptyDetail: 'Créez des entreprises depuis les outils CRM puis actualisez cette vue.',
        noResultsTitle: 'Aucun compte client ne correspond aux filtres actuels.',
        noResultsDetail: 'Ajustez la recherche, les statuts sélectionnés, les tags sélectionnés ou le tri pour élargir le portefeuille.',
        searchPlaceholder: 'Rechercher un client',
        statusFilterLabel: 'Statut',
        tagFilterLabel: 'Tags',
        sortLabel: 'Trier par',
        sortPriority: 'Travail actif et couverture',
        sortName: 'Nom',
        sortContractRevenue: 'Revenu des contrats',
        industry: 'Secteur',
        employees: 'Employés',
        revenue: 'Chiffre d\'affaires',
        contractRevenue: 'Revenu des contrats',
        source: 'Source',
        tagsAria: 'Tags des comptes clients',
        openCompanyAria: 'Ouvrir le détail de {{companyName}}'
      },
      groupMembers: {
        title: 'Membres du groupe',
        subtitle: '{{memberCount}} visibles dans le groupe sélectionné, avec leurs contrats actifs.',
        toolbarEyebrow: 'Compétences de l’équipe',
        toolbarCopy: 'Filtrez le groupe sélectionné par tags de compétences pour vous concentrer sur les bonnes personnes.',
        filteredCopy: '{{count}} membres correspondent aux tags de compétences sélectionnés.',
        tagFilterLabel: 'Tags de compétences',
        loading: 'Chargement des membres du groupe...',
        emptyTitle: 'Aucun membre n\'est encore visible dans ce groupe.',
        emptyDetail: 'Sélectionnez un groupe avec des utilisateurs pour afficher les missions et le bench.',
        noResultsTitle: 'Aucun membre ne correspond aux tags de compétences sélectionnés.',
        noResultsDetail: 'Ajustez ou videz le filtre de tags actuel pour élargir la liste des membres.',
        benchTitle: 'Bench',
        benchSubtitle: 'Membres sans contrat actif pour le moment.',
        staffedTitle: 'En mission',
        staffedSubtitle: 'Membres actuellement staffés sur des contrats actifs.',
        emptyBench: 'Personne n\'est actuellement sur le bench.',
        emptyStaffed: 'Aucune affectation active n\'est visible dans ce groupe.',
        skillTags: 'Tags de compétences',
        staffedCount: '{{count}} en mission',
        benchCount: '{{count}} sur le bench',
        contractCount: '{{count}} contrat(s)',
        contractMeta: '{{companyName}} · {{contractLabel}}',
        contractDateRange: '{{startDate}} - {{endDate}}',
        contractUntil: 'Jusqu\'au {{endDate}}',
        contractFrom: 'Depuis le {{startDate}}',
        dialog: {
          openProfile: 'Ouvrir le profil de {{memberName}}',
          jobTitle: 'Poste',
          phone: 'Téléphone',
          linkedInUrl: 'LinkedIn',
          totalExperience: 'Expérience totale',
          currentRoleExperience: 'Expérience dans le poste',
          coreSkills: 'Compétences principales',
          previousExperiences: 'Expériences précédentes',
          upskillingGoals: 'Souhaits de montée en compétences',
          activeContracts: 'Contrats actifs',
          noActiveContracts: 'Aucun contrat actif n’est actuellement visible pour ce membre.',
          openContract: 'Ouvrir le contrat'
        }
      },
      groupAnalysis: {
        title: 'Analyse du portefeuille',
        subtitle: 'Historique des analyses portefeuille enregistrées pour {{groupName}}.',
        toolbarEyebrow: 'Analyse de groupe',
        toolbarCopy: 'Consultez la dernière analyse générée par l’agent pour le groupe sélectionné et parcourez les snapshots précédents stockés dans le CRM.',
        generate: 'Générer une nouvelle analyse',
        generating: 'Travail en cours',
        loading: 'Chargement de l\'analyse du portefeuille...',
        emptyTitle: 'Aucune analyse portefeuille enregistrée n\'est encore disponible.',
        emptyDetail: 'Demandez à l’agent de générer l’analyse portefeuille du groupe, puis actualisez cette vue.',
        loadErrorTitle: 'Impossible de charger l\'analyse du portefeuille.',
        historyTitle: 'Historique',
        historySubtitle: 'Snapshots précédents dans la langue courante.',
        emptyHistory: 'Aucune analyse précédente n’est encore enregistrée.',
        sections: {
          analysisDate: 'Date de l\'analyse',
          portfolioOverview: 'Vue d\'ensemble du portefeuille',
          sectorAnalysisAndTransformationSignals: 'Analyse sectorielle & signaux de transformation',
          commercialMaturityEvaluation: 'Évaluation de la maturité commerciale',
          priorityCommercialOpportunities: 'Opportunités commerciales prioritaires',
          portfolioGovernanceRecommendations: 'Recommandations de gouvernance du portefeuille',
          summary: 'Synthèse',
          immediateNextStep: 'Prochaine étape immédiate (pour vous)'
        }
      }
    },
    companyDetail: {
      hero: {
        eyebrow: 'Fiche entreprise',
        loadingTitle: 'Chargement de l\'entreprise...',
        loadingCopy: 'Récupération des derniers détails de l\'entreprise depuis le CRM.',
        errorTitle: 'Impossible de charger l\'entreprise',
        unavailableTitle: 'La page de détail de l\'entreprise est indisponible pour le moment.',
        unavailableDetail: 'Revenez à l\'annuaire puis réessayez.',
        deleteCompany: 'Supprimer l\'entreprise du CRM',
        deleting: 'Suppression...',
        deleteError: 'Impossible de supprimer l\'entreprise.',
        summaryAria: 'Résumé de la fiche entreprise',
        missingDescription: 'Aucune description d\'entreprise n\'est encore disponible.',
        location: 'Localisation',
        mainContact: 'Contact principal',
        statRevenue: 'Chiffre d\'affaires annuel',
        statOpenActions: 'Actions ouvertes',
        statActiveContracts: 'Contrats actifs',
        statTags: 'Tags du compte'
      },
      navigation: {
        ariaLabel: 'Sections de la fiche entreprise',
        overview: 'Vue d\'ensemble',
        strategic: 'Stratégique',
        customers: 'Clients',
        comments: 'Commentaires',
        actions: 'Actions',
        contracts: 'Contrats'
      },
      overview: {
        title: 'Vue d\'ensemble',
        subtitle: 'Informations cœur de compte et références liées.',
        businessSnapshot: 'Vue business',
        relationshipRhythm: 'Rythme de relation',
        channels: 'Canaux',
        industry: 'Secteur',
        location: 'Localisation',
        employeeCount: 'Effectif',
        annualRevenue: 'Chiffre d\'affaires annuel',
        leadSource: 'Source du lead',
        ownerUser: 'Responsable du compte',
        saveOwnerUser: 'Enregistrer le responsable',
        ownerUpdateError: 'Impossible de mettre à jour le responsable du compte.',
        ownerGroup: 'Groupe propriétaire',
        mainContact: 'Contact principal',
        parentCompany: 'Société mère',
        tags: 'Tags',
        tagsAria: 'Tags de l\'entreprise',
        websiteAction: 'Ouvrir le site web',
        linkedInAction: 'Ouvrir LinkedIn'
      },
      communication: {
        title: 'Communication',
        subtitle: 'Canaux de l\'entreprise et chronologie de la relation.',
        website: 'Site web',
        linkedIn: 'LinkedIn',
        notes: 'Notes'
      },
      strategic: {
        title: 'Vue stratégique',
        subtitle: 'Signaux business et contexte commercial de ce compte.',
        emptyTitle: 'Aucune information stratégique n\'est enregistrée pour cette entreprise.',
        emptyDetail: 'Mettez à jour la stratégie, les actualités récentes et les opportunités commerciales depuis les outils CRM.',
        coverage: 'Couverture',
        currentStrategy: 'Stratégie actuelle',
        recentNews: 'Actualités récentes',
        commercialOpportunities: 'Opportunités commerciales',
        recommendedNextActions: 'Prochaines actions recommandées',
        missingCurrentStrategy: 'Aucune stratégie actuelle n\'est enregistrée.',
        missingRecentNews: 'Aucune actualité récente n\'est enregistrée.',
        missingCommercialOpportunities: 'Aucune opportunité commerciale n\'est enregistrée.',
        missingRecommendedNextActions: 'Aucune prochaine action recommandée n\'est enregistrée.'
      },
      customers: {
        title: 'Clients',
        subtitle: 'Personnes rattachées à ce compte entreprise.',
        kindBadge: 'Contact',
        toolbarEyebrow: 'Cartographie de relation',
        toolbarCopy: 'Recherchez les contacts du compte et gardez la fiche sélectionnée ouverte dans un espace dédié.',
        filteredCopy: '{{count}} contacts correspondent aux filtres actuels.',
        searchPlaceholder: 'Rechercher par nom, rôle, e-mail ou propriétaire',
        filterAll: 'Tous les contacts',
        filterMain: 'Contact principal',
        filterAssigned: 'Assignés',
        filterUnassigned: 'Non assignés',
        actionFailed: 'L\'action client a échoué.',
        newHeading: 'Nouveau client',
        createLabel: 'Créer le client',
        emptyTitle: 'Aucun client n\'est rattaché à cette entreprise.',
        emptyDetail: 'Ajoutez des fiches client liées à cette entreprise pour alimenter cette section.',
        noResultsTitle: 'Aucun contact ne correspond aux filtres actuels.',
        noResultsDetail: 'Ajustez la recherche ou les filtres pour élargir le résultat.',
        missingJobTitle: 'Poste indisponible',
        loadingEditor: 'Chargement de l\'éditeur client...',
        loadingEditorDetail: 'Récupération des derniers détails du client depuis le CRM.',
        detailHeading: 'Détail du client',
        editHeading: 'Modifier le client',
        workspaceTitle: 'Sélectionnez un contact pour le consulter ou le modifier depuis l\'espace épinglé.',
        workspaceDetail: 'Le contact sélectionné reste ouvert ici pendant que la liste reste visible.',
        loadError: 'Impossible de charger les détails du client.',
        createError: 'Impossible de créer le client.',
        updateError: 'Impossible de mettre à jour le client.',
        deleteError: 'Impossible de supprimer le client.',
        mainContactError: 'Impossible de mettre à jour le contact principal.',
        noRelationshipOwner: 'Aucun responsable de relation',
        linkedInAction: 'Ouvrir LinkedIn'
      },
      contracts: {
        title: 'Contrats',
        subtitle: 'Engagements commerciaux rattachés à cette entreprise.',
        kindBadge: 'Contrat',
        toolbarEyebrow: 'Portefeuille de contrats',
        toolbarCopy: 'Consultez les engagements en cours, à venir et terminés sans quitter la fiche entreprise.',
        filteredCopy: '{{count}} contrats correspondent aux filtres actuels.',
        searchPlaceholder: 'Rechercher par client ou offre',
        filterAll: 'Tous les contrats',
        filterActive: 'Actifs',
        filterUpcoming: 'À venir',
        filterEnded: 'Terminés',
        actionFailed: 'L\'action contrat a échoué.',
        newHeading: 'Nouveau contrat',
        createLabel: 'Créer le contrat',
        emptyTitle: 'Aucun contrat n\'est rattaché à cette entreprise.',
        emptyDetail: 'Les contrats liés dans le CRM apparaîtront ici.',
        noResultsTitle: 'Aucun contrat ne correspond aux filtres actuels.',
        noResultsDetail: 'Élargissez la recherche ou changez de filtre de cycle de vie.',
        dailyRate: 'Taux journalier',
        duration: 'Durée',
        endDate: 'Date de fin',
        customer: 'Client',
        loadingEditor: 'Chargement de l\'éditeur contrat...',
        loadingEditorDetail: 'Récupération des derniers détails du contrat depuis le CRM.',
        detailHeading: 'Détail du contrat',
        editHeading: 'Modifier le contrat',
        workspaceTitle: 'Sélectionnez un contrat pour inspecter ses détails commerciaux côte à côte.',
        workspaceDetail: 'L\'espace contrat reste épinglé pendant la navigation dans le portefeuille.',
        lifecycle: {
          active: 'Actif',
          upcoming: 'À venir',
          ended: 'Terminé'
        },
        loadError: 'Impossible de charger les détails du contrat.',
        createError: 'Impossible de créer le contrat.',
        updateError: 'Impossible de mettre à jour le contrat.',
        deleteError: 'Impossible de supprimer le contrat.'
      },
      rex: {
        title: 'REX',
        subtitle: 'Retours d’expérience structurés liés aux contrats.',
        toolbarEyebrow: 'Retour d’expérience',
        toolbarCopy: 'Capitalisez les challenges, solutions et valeur livrée pour les missions en cours ou terminées.',
        filteredCopy: '{{count}} REX correspondent aux filtres actuels.',
        searchPlaceholder: 'Rechercher par titre, contrat, consultant ou contenu',
        addAction: 'Ajouter un REX',
        actionFailed: 'L’opération sur le REX a échoué.',
        newHeading: 'Nouveau REX',
        detailHeading: 'Détail du REX',
        editHeading: 'Modifier le REX',
        createLabel: 'Créer le REX',
        saveLabel: 'Enregistrer le REX',
        emptyTitle: 'Aucun REX n’est encore rattaché à cette entreprise.',
        emptyDetail: 'Ajoutez des retours structurés issus des contrats pour conserver l’expérience réutilisable dans le CRM.',
        noContractsTitle: 'Aucun contrat disponible pour les REX.',
        noContractsDetail: 'Créez un contrat avant d’ajouter un retour d’expérience.',
        noResultsTitle: 'Aucun REX ne correspond aux filtres actuels.',
        noResultsDetail: 'Élargissez la recherche pour afficher davantage de retours d’expérience.',
        unknownContract: 'Contrat inconnu',
        untitled: 'REX sans titre',
        noConsultants: 'Aucun consultant listé',
        emptySection: 'Aucun contenu enregistré.',
        loadingEditor: 'Chargement de l’éditeur REX...',
        loadingEditorDetail: 'Récupération des derniers détails du REX depuis le CRM.',
        loadError: 'Impossible de charger les détails du REX.',
        createError: 'Impossible de créer le REX.',
        updateError: 'Impossible de mettre à jour le REX.',
        deleteError: 'Impossible de supprimer le REX.',
        fields: {
          contract: 'Contrat',
          title: 'Titre',
          description: 'Description',
          challenges: 'CHALLENGES',
          solution: 'SOLUTION',
          resultsAndValueAdded: 'RESULTS AND VALUE ADDED'
        }
      },
      actions: {
        title: 'Actions',
        subtitle: 'Prochaines étapes, suivis et tâches internes rattachés à ce compte.',
        kindBadge: 'Action',
        toolbarEyebrow: 'Pilotage d\'exécution',
        toolbarCopy: 'Suivez ce qui avance, bloqué ou est en retard directement depuis la vue compte.',
        filteredCopy: '{{count}} actions correspondent aux filtres actuels.',
        searchPlaceholder: 'Rechercher par titre, propriétaire, client ou offre',
        filterAll: 'Toutes les actions',
        filterOpen: 'Ouvertes',
        filterOverdue: 'En retard',
        filterDone: 'Terminées',
        actionFailed: 'L\'opération sur l\'action a échoué.',
        newHeading: 'Nouvelle action',
        createLabel: 'Créer l\'action',
        emptyTitle: 'Aucune action n\'est rattachée à cette entreprise.',
        emptyDetail: 'Suivez les relances, réunions et tâches internes depuis ce panneau.',
        noResultsTitle: 'Aucune action ne correspond aux filtres actuels.',
        noResultsDetail: 'Élargissez la recherche ou changez de statut.',
        missingTitle: 'Action sans titre',
        noDueDate: 'Aucune date d\'échéance',
        loadingEditor: 'Chargement de l\'éditeur d\'action...',
        loadingEditorDetail: 'Récupération des derniers détails de l\'action depuis le CRM.',
        detailHeading: 'Détail de l\'action',
        editHeading: 'Modifier l\'action',
        workspaceTitle: 'Sélectionnez une action pour revoir son contexte d\'exécution et la mettre à jour.',
        workspaceDetail: 'L\'espace de travail de l\'action reste visible pendant la navigation dans la liste.',
        loadError: 'Impossible de charger les détails de l\'action.',
        createError: 'Impossible de créer l\'action.',
        updateError: 'Impossible de mettre à jour l\'action.',
        deleteError: 'Impossible de supprimer l\'action.'
      },
      comments: {
        title: 'Notes',
        subtitle: 'Notes typées et fichiers de contexte rattachés à ce compte dans le temps.',
        toolbarEyebrow: 'Notes du compte',
        toolbarCopy: 'Consignez interactions, réflexions et opportunités directement depuis la page entreprise.',
        filteredCopy: '{{count}} notes correspondent aux filtres actuels.',
        searchPlaceholder: 'Rechercher par contenu, type ou auteur',
        filterAll: 'Toutes les notes',
        actionFailed: 'L\'opération sur la note a échoué.',
        newHeading: 'Nouvelle note',
        createLabel: 'Créer la note',
        emptyTitle: 'Aucune note n\'est rattachée à cette entreprise.',
        emptyDetail: 'Ajoutez des notes pour construire l\'historique relationnel et faire remonter les signaux commerciaux.',
        noResultsTitle: 'Aucune note ne correspond aux filtres actuels.',
        noResultsDetail: 'Ajustez les termes de recherche pour élargir le résultat.',
        emptyEntry: 'Aucun contenu de note n\'est enregistré.',
        loadingEditor: 'Chargement de l\'éditeur de note...',
        loadingEditorDetail: 'Récupération des derniers détails de la note depuis le CRM.',
        detailHeading: 'Détail de la note',
        editHeading: 'Modifier la note',
        loadError: 'Impossible de charger les détails de la note.',
        createError: 'Impossible de créer la note.',
        updateError: 'Impossible de mettre à jour la note.',
        deleteError: 'Impossible de supprimer la note.',
        documentsUploadError: 'Impossible de téléverser le document de la note.',
        documentsDownloadError: 'Impossible de télécharger le document de la note.',
        documentsDeleteError: 'Impossible de supprimer le document de la note.'
      },
      documents: {
        title: 'Documents',
        subtitle: 'Fichiers rattachés à ce compte et préparés pour un futur usage par le LLM.',
        toolbarEyebrow: 'Coffre documentaire',
        toolbarCopy: '{{count}} document(s) sont actuellement rattachés à cette entreprise.',
        toolbarEmpty: 'Téléversez ici les documents du compte pour qu’ils puissent ensuite être exploités par les outils CRM et les agents.',
        maxFileSize: 'Taille maximale : 50 Mo par fichier.',
        selectFile: 'Sélectionner un fichier',
        upload: 'Téléverser le document',
        uploading: 'Téléversement...',
        emptyTitle: 'Aucun document n’est encore rattaché à cette entreprise.',
        emptyDetail: 'Téléversez des PDF, documents Word, PowerPoint ou Excel pour conserver le contexte du compte dans le CRM.',
        errorTitle: 'L’opération sur le document a échoué.',
        download: 'Télécharger',
        deleteConfirm: 'Supprimer {{fileName}} de cette entreprise ?',
        status: {
          pending: 'Extraction en attente',
          completed: 'Texte extrait',
          failed: 'Extraction échouée'
        },
        webLinks: {
          eyebrow: 'Liens externes',
          copy: 'Référencez les sites ou documents utiles hébergés hors du CRM.',
          label: 'Libellé du lien',
          url: 'URL',
          urlPlaceholder: 'https://example.com/ressource',
          description: 'Description',
          addAction: 'Ajouter le lien',
          empty: 'Aucun lien web externe n’est encore rattaché à cette entreprise.',
          unnamed: 'Lien externe',
          deleteConfirm: 'Retirer {{label}} de cette entreprise ?'
        }
      }
    },
    commentDetail: {
      eyebrow: 'Éditeur de note',
      headings: {
        details: 'Détail de la note'
      },
      sections: {
        entry: 'Contenu de la note',
        entryCopy: 'Consignez l\'interaction, la réflexion ou l\'opportunité à conserver dans l\'historique du compte.'
      },
      fields: {
        type: 'Type',
        customer: 'Client lié',
        contract: 'Contrat lié',
        author: 'Auteur',
        createdAt: 'Créé le',
        updatedAt: 'Mis à jour le',
        updatedBy: 'Mis à jour par',
        content: 'Note'
      },
      placeholders: {
        content: 'Saisissez le résumé de l\'interaction, la réflexion, l\'opportunité ou le contexte relationnel à conserver dans l\'historique de l\'entreprise.'
      },
      hints: {
        edited: 'Cette note a été modifiée après sa création initiale.'
      },
      empty: {
        content: 'Aucun contenu de note n\'est enregistré.'
      },
      customer: {
        companyWide: 'Note au niveau entreprise'
      },
      contract: {
        none: 'Aucun contrat lié'
      },
      documents: {
        eyebrow: 'Documents joints',
        createHint: 'Enregistrez d\'abord la note, puis joignez les documents de contexte.',
        editCopy: 'Joignez directement les fichiers de contexte à cette note.',
        readOnlyCopy: 'Téléchargez les fichiers de contexte rattachés à cette note.',
        selectFile: 'Sélectionner un fichier',
        upload: 'Téléverser le document',
        uploading: 'Téléversement...',
        empty: 'Aucun document n\'est encore rattaché à cette note.',
        deleteConfirm: 'Supprimer {{fileName}} de cette note ?'
      }
    },
    actionDetail: {
      eyebrow: 'Éditeur d\'action',
      headings: {
        details: 'Détail de l\'action'
      },
      sections: {
        core: 'Configuration principale',
        coreCopy: 'Définissez ce qui doit se passer et qui en est responsable.',
        relationships: 'Liens commerciaux',
        relationshipsCopy: 'Rattachez l\'action au bon client, contrat et à la bonne offre.',
        planning: 'Calendrier',
        planningCopy: 'Renseignez le timing attendu pour la réalisation et la clôture.',
        context: 'Contexte et résultat',
        contextCopy: 'Documentez le brief, le résultat attendu et le résultat obtenu.'
      },
      fields: {
        title: 'Titre',
        description: 'Description',
        type: 'Type',
        status: 'Statut',
        priority: 'Priorité',
        dueDate: 'Date d\'échéance',
        dueTime: 'Heure d\'échéance',
        scheduledAt: 'Planifiée le',
        scheduledTime: 'Heure planifiée',
        completedAt: 'Terminée le',
        completedTime: 'Heure de fin',
        ownerUserId: 'Responsable',
        customer: 'Client',
        contract: 'Contrat',
        offering: 'Offre',
        outcome: 'Résultat',
        reminderEnabled: 'Rappel activé'
      },
      placeholders: {
        title: 'Relancer après la revue de proposition',
        description: 'Décrivez la prochaine étape attendue et le contexte pour l\'équipe.',
        outcome: 'Consignez le résultat une fois l\'action terminée.'
      },
      hints: {
        lockedByContract: 'Le client et l\'offre sont verrouillés car ils sont hérités du contrat sélectionné.'
      },
      empty: {
        description: 'Aucune description n\'est enregistrée.',
        outcome: 'Aucun résultat n\'est enregistré.'
      }
    },
    customerDetail: {
      eyebrow: 'Éditeur client',
      headings: {
        details: 'Détail du client'
      },
      sections: {
        identity: 'Identité et canaux',
        identityCopy: 'Renseignez l\'identité principale et les moyens de contact directs.',
        relationship: 'Responsabilité de la relation',
        relationshipCopy: 'Précisez la maturité de la relation et la personne qui la porte.',
        context: 'Notes de contexte',
        contextCopy: 'Documentez les motivations, risques et éléments de contexte utiles à l\'équipe.'
      },
      fields: {
        firstName: 'Prénom',
        lastName: 'Nom',
        jobTitle: 'Poste',
        phoneNumber: 'Numéro de téléphone',
        email: 'E-mail',
        linkedInUrl: 'URL LinkedIn',
        relationshipStatus: 'Statut de relation',
        inRelationshipWith: 'En relation avec',
        keyInterests: 'Centres d\'intérêt clés',
        keyConcerns: 'Points de vigilance',
        comments: 'Commentaires'
      },
      placeholders: {
        firstName: 'Morgan',
        lastName: 'Lee',
        jobTitle: 'Responsable partenariats',
        phoneNumber: '+33 6 12 34 56 78',
        email: 'morgan.lee@example.com',
        linkedInUrl: 'https://www.linkedin.com/in/example',
        keyInterests: 'Sujets, produits et priorités importants pour ce client.',
        keyConcerns: 'Risques, blocages et objections à garder en tête.',
        comments: 'Notes de relation, contexte et interactions récentes.'
      },
      empty: {
        keyInterests: 'Aucun centre d\'intérêt clé n\'est enregistré.',
        keyConcerns: 'Aucun point de vigilance n\'est enregistré.',
        comments: 'Aucun commentaire n\'est enregistré.'
      },
      linkedNotes: {
        title: 'Notes liées',
        copy: 'Consultez les notes CRM explicitement rattachées à ce client.',
        empty: 'Aucune note n\'est encore liée à ce client.',
        openAction: 'Voir la note',
        editAction: 'Modifier la note'
      }
    },
    offeringDetail: {
      eyebrow: 'Éditeur d\'offre',
      headings: {
        new: 'Nouvelle offre',
        details: 'Détail de l\'offre'
      },
      fields: {
        name: 'Nom',
        offeringType: 'Type d\'offre',
        description: 'Description'
      },
      placeholders: {
        name: 'Atelier de découverte',
        description: 'Expliquez ce que cette offre apporte et dans quels cas elle doit être proposée.'
      },
      actions: {
        create: 'Créer l\'offre',
        save: 'Enregistrer l\'offre'
      },
      documents: {
        eyebrow: 'Documents de l\'offre',
        title: 'Documents joints',
        toolbarCopy: '{{count}} document(s) sont actuellement rattachés à cette offre.',
        toolbarEmpty: 'Téléversez ici les supports liés à l\'offre pour qu\'ils restent disponibles dans le flux documentaire du CRM.',
        maxFileSize: 'Taille maximale : 50 Mo par fichier.',
        selectFile: 'Sélectionner un fichier',
        upload: 'Téléverser le document',
        uploading: 'Téléversement...',
        loading: 'Chargement des documents...',
        emptyTitle: 'Aucun document n\'est encore rattaché à cette offre.',
        emptyDetail: 'Téléversez des PDF, documents Word, PowerPoint ou Excel liés à cette offre.',
        errorTitle: 'L\'opération sur le document a échoué.',
        download: 'Télécharger',
        deleteConfirm: 'Supprimer {{fileName}} de cette offre ?',
        status: {
          pending: 'Extraction en attente',
          completed: 'Texte extrait',
          failed: 'Extraction échouée'
        }
      },
      empty: {
        description: 'Aucune description d\'offre n\'est enregistrée.'
      }
    },
    contractDetail: {
      eyebrow: 'Éditeur contrat',
      headings: {
        details: 'Détail du contrat'
      },
      sections: {
        scope: 'Périmètre',
        scopeCopy: 'Rattachez l\'engagement au bon client et à la bonne offre.',
        timeline: 'Calendrier',
        timelineCopy: 'Gardez les dates et la durée alignées avec l\'engagement réel.',
        commercials: 'Dimensions commerciales',
        commercialsCopy: 'Suivez la valeur globale et l\'économie journalière.',
        staffing: 'Staffing',
        staffingCopy: 'Affectez un ou plusieurs consultants du groupe ou enregistrez des consultants externes nommés.'
      },
      fields: {
        customer: 'Client',
        offering: 'Offre',
        startDate: 'Date de début',
        endDate: 'Date de fin',
        durationInDays: 'Durée en jours',
        totalInvoice: 'Facturation totale',
        dailyRate: 'Taux journalier',
        consultants: 'Consultants',
        groupConsultant: 'Consultant du groupe',
        externalConsultant: 'Consultant externe'
      },
      actions: {
        addGroupConsultant: 'Ajouter un consultant du groupe',
        addExternalConsultant: 'Ajouter un consultant externe'
      },
      consultants: {
        groupMember: 'Membre du groupe',
        external: 'Externe',
        selectGroupMember: 'Sélectionnez un membre du groupe',
        empty: 'Aucun consultant n\'est encore affecté à ce contrat.'
      },
      lifecycle: {
        active: 'Actif',
        upcoming: 'À venir',
        ended: 'Terminé'
      },
      linkedNotes: {
        title: 'Notes liées',
        copy: 'Consultez les notes CRM explicitement rattachées à ce contrat.',
        empty: 'Aucune note n\'est encore liée à ce contrat.',
        openAction: 'Voir la note',
        editAction: 'Modifier la note'
      }
    },
    admin: {
      hero: {
        eyebrow: 'Administration',
        title: 'Gestion des groupes utilisateurs',
        copy: 'Consultez les groupes existants, créez-en de nouveaux et gardez une vue claire sur les membres et les admins.',
        summaryAria: 'Résumé de la page admin'
      },
      access: {
        title: 'L\'espace admin n\'est pas disponible',
        subtitle: 'Vous devez administrer au moins un groupe pour utiliser cet espace.',
        detail: 'Rejoignez le groupe ADMIN pour créer de nouveaux groupes, ou devenez admin d\'un groupe existant pour gérer ses membres.'
      },
      create: {
        title: 'Créer un groupe',
        subtitle: 'Provisionnez un nouveau groupe pour les permissions CRM et la collaboration.',
        groupName: 'Nom du groupe',
        placeholder: 'Exemple : Sales Operations',
        currentDirectory: 'Annuaire actuel',
        adminCoverage: 'Couverture admin',
        manageableGroups: 'Groupes gérables',
        restricted: 'Seuls les membres du groupe ADMIN peuvent créer de nouveaux groupes.',
        creating: 'Création...'
      },
      delete: {
        title: 'Supprimer un groupe',
        subtitle: 'Supprimez un groupe inutilisé lorsque vous disposez d\'un accès système ADMIN.',
        group: 'Groupe à supprimer',
        hint: 'Le groupe système ADMIN ne peut pas être supprimé. Les groupes qui possèdent encore des entreprises sont bloqués côté backend.',
        empty: 'Aucun groupe supprimable n\'est actuellement disponible.',
        action: 'Supprimer le groupe',
        deleting: 'Suppression...',
        confirm: 'Supprimer le groupe "{{groupName}}" ? Cette action est irréversible.'
      },
      membership: {
        title: 'Ajouter un utilisateur à un groupe',
        subtitle: 'Ajoutez un membre ou un admin dans l\'un des groupes que vous gérez.',
        editTitle: 'Modifier un utilisateur visible',
        editSubtitle: 'Mettez à jour le profil et le rôle de l\'utilisateur sélectionné dans {{groupName}}.',
        empty: 'Vous n\'êtes admin d\'aucun groupe pour le moment.',
        group: 'Groupe cible',
        userId: 'Identifiant OAuth',
        userIdPlaceholder: 'provider|user-id',
        role: 'Rôle',
        memberRole: 'Membre',
        adminRole: 'Admin',
        firstName: 'Prénom',
        lastName: 'Nom',
        email: 'E-mail',
        phone: 'Téléphone',
        linkedInUrl: 'URL LinkedIn',
        linkedInUrlPlaceholder: 'https://www.linkedin.com/in/example',
        experienceStartDate: 'Date de début d\'expérience',
        jobTitle: 'Poste',
        currentRoleStartDate: 'Date de début dans le poste',
        coreSkills: 'Compétences principales',
        previousExperiences: 'Expériences précédentes',
        upskillingGoals: 'Souhaits de montée en compétences',
        skillTagsPreview: 'Aperçu des tags de compétences',
        skillTagsHint: 'Utilisez des virgules, des points-virgules ou des retours à la ligne dans le champ compétences pour gérer les tags.',
        dialogHint: 'Utilisez la dialog dédiée pour ajouter un utilisateur, puis le bouton Edit des cartes de l’annuaire pour modifier un profil existant.',
        addAction: 'Ajouter l\'utilisateur',
        saveAction: 'Enregistrer l\'utilisateur',
        cancelEdit: 'Annuler la modification',
        editLockedHint: 'Le groupe et l\'identifiant OAuth restent verrouillés pendant la modification d\'un utilisateur existant.',
        saving: 'Enregistrement...'
      },
      directory: {
        title: 'Groupes gérés',
        subtitle: 'Groupes pour lesquels vous disposez actuellement des droits d\'administration.',
        loading: 'Chargement des groupes...',
        emptyTitle: 'Aucun groupe gérable n\'est encore disponible.',
        emptyDetail: 'Créez un groupe depuis l\'accès ADMIN ou demandez à être ajouté comme admin d\'un groupe existant.',
        adminGroup: 'Groupe admin',
        members: 'Membres',
        admins: 'Admins',
        fileStorage: 'Stockage : {{size}}',
        documentsAction: 'Fichiers',
        deleteAction: 'Supprimer l\'utilisateur',
        noAdmins: 'Aucun admin n\'est enregistré dans ce groupe.',
        noMembers: 'Aucun membre n\'est enregistré dans ce groupe.',
        documentsDialog: {
          title: 'Fichiers de discussion de {{userName}}',
          loading: 'Chargement des fichiers de discussion...',
          empty: 'Aucun fichier de discussion n’est actuellement stocké pour cet utilisateur.',
          deleteConfirm: 'Supprimer "{{fileName}}" de l’historique de discussion de cet utilisateur ?',
          untitledConversation: 'Discussion {{shortId}}'
        },
        deleteDialog: {
          title: 'Supprimer {{userName}}',
          loading: 'Chargement de l’impact de suppression...',
          intro: 'Cette action supprime le profil CRM, toutes les appartenances aux groupes, les discussions stockées et les fichiers joints de cet utilisateur.',
          summary: '{{groups}} appartenances à des groupes, {{conversations}} discussions et {{documents}} documents seront supprimés.',
          confirmCopy: 'Supprimer ce profil utilisateur et tout le contenu listé ? Cette action est irréversible.',
          blockedTitle: 'La suppression est actuellement bloquée.',
          blockedSelf: 'Vous ne pouvez pas supprimer votre propre profil utilisateur.',
          blockedMissingAccess: 'Cet utilisateur appartient encore à des groupes que vous n’administrez pas.',
          blockedLastAdmin: '"{{groupName}}" se retrouverait sans administrateur.',
          groupsTitle: 'Appartenances aux groupes',
          groupsEmpty: 'Aucune appartenance à un groupe n’est actuellement enregistrée pour cet utilisateur.',
          conversationsTitle: 'Discussions',
          conversationsEmpty: 'Aucune discussion stockée ne sera supprimée.',
          conversationMeta: '{{messages}} messages · {{documents}} documents',
          standaloneDocumentsTitle: 'Fichiers autonomes',
          standaloneDocumentsEmpty: 'Aucun fichier autonome ne sera supprimé.',
          untitledConversation: 'Discussion {{shortId}}',
          groupRoleAdmin: 'Admin',
          groupRoleMember: 'Membre',
          groupAdminGroup: 'Groupe système',
          groupNotManaged: 'Non administré par vous',
          groupLastAdmin: 'Dernier admin',
          close: 'Annuler',
          action: 'Supprimer l\'utilisateur',
          deleting: 'Suppression...'
        }
      },
      databaseMaintenance: {
        title: 'Maintenance de la base',
        subtitle: 'Les backups quotidiens sont stockés côté serveur. Seuls les membres du groupe ADMIN peuvent déclencher un backup ou restaurer la base.',
        backupsTitle: 'Backups disponibles',
        backupsCopy: 'Fichiers de backup récents stockés par le serveur.',
        loading: 'Chargement des backups...',
        empty: 'Aucun fichier de backup n\'est encore disponible.',
        downloadAction: 'Télécharger',
        createBackup: 'Créer un backup maintenant',
        creatingBackup: 'Création du backup...',
        restoreTitle: 'Restaurer la base',
        restoreCopy: 'Téléversez un fichier de backup pour remplacer la base actuelle. Un backup de sécurité est créé automatiquement avant la restauration.',
        restoreConfirm: 'Restaurer toute la base de données à partir du fichier sélectionné ? Les données actuelles seront remplacées.',
        restoreFileLabel: 'Fichier de backup',
        restoreSelectedFile: 'Fichier sélectionné : {{fileName}}',
        restoreHint: 'Fichiers acceptés : .gz ou .json.gz générés par le service de backup du CRM.',
        restoreAction: 'Restaurer la base',
        restoring: 'Restauration...',
        restoreSuccess: 'Restauration terminée à partir de {{fileName}}.',
        restoreSummary: '{{collections}} collections et {{documents}} documents ont été restaurés.',
        preRestoreBackup: 'Backup de sécurité créé avant restauration : {{fileName}}',
        lastBackupCreated: 'Backup créé : {{fileName}}',
        triggers: {
          manual: 'Manuel',
          scheduled: 'Planifié',
          pre_restore: 'Pré-restauration'
        }
      }
    },
    userDetail: {
      hero: {
        eyebrow: 'Utilisateur CRM',
        loadingTitle: 'Chargement du profil utilisateur',
        loadingCopy: 'Le profil CRM est en cours de récupération.',
        errorTitle: 'Profil utilisateur indisponible',
        unavailableTitle: 'Profil utilisateur indisponible',
        unavailableDetail: 'Cet utilisateur n est pas accessible avec votre visibilité CRM actuelle.'
      }
    },
    profile: {
      title: 'Profil',
      hero: {
        eyebrow: 'Profil personnel',
        title: 'Votre profil',
        copy: 'Mettez à jour les informations personnelles rattachées à votre compte OAuth.',
        skillTags: 'Tags de compétences'
      },
      form: {
        title: 'Détails du profil',
        subtitle: 'Ces champs sont stockés dans le CRM et réutilisés dans les écrans d\'administration.',
        loading: 'Chargement de votre profil...',
        firstName: 'Prénom',
        lastName: 'Nom',
        email: 'E-mail',
        phone: 'Téléphone',
        linkedInUrl: 'URL LinkedIn',
        linkedInUrlPlaceholder: 'https://www.linkedin.com/in/example',
        experienceStartDate: 'Date de début d\'expérience',
        jobTitle: 'Poste',
        currentRoleStartDate: 'Date de début dans le poste',
        coreSkills: 'Compétences principales',
        previousExperiences: 'Expériences précédentes',
        upskillingGoals: 'Souhaits de montée en compétences',
        tags: 'Tags de compétences',
        tagsHint: 'Ajoutez ou supprimez les tags qui décrivent le mieux votre profil de compétences actuel.',
        tagsAddLabel: 'Ajouter un tag',
        tagsPlaceholder: 'Exemple : sales',
        tagsAddAction: 'Ajouter le tag',
        tagsEmpty: 'Aucun tag de compétence n’est actuellement défini.',
        skillTagsPreview: 'Aperçu des tags de compétences',
        skillTagsHint: 'Utilisez des virgules, des points-virgules ou des retours à la ligne dans le champ compétences pour gérer les tags.',
        language: 'Langue',
        theme: 'Thème',
        save: 'Enregistrer le profil',
        saveSuccess: 'Le profil a bien été enregistré.',
        saving: 'Enregistrement...'
      },
        documents: {
          title: 'Documents publiés',
          subtitle: 'Tous les documents que vous avez téléversés dans vos conversations de chat.',
          loading: 'Chargement de vos documents...',
          empty: 'Aucun document n’a encore été téléversé.',
          deleteConfirm: 'Supprimer "{{fileName}}" de vos fichiers de discussion ?',
          downloadAction: 'Télécharger',
          downloading: 'Téléchargement...'
        },
        conversations: {
          title: 'Discussions',
          subtitle: 'Parcourez vos discussions de chat récentes, consultez leurs messages et supprimez celles dont vous n’avez plus besoin.',
          loading: 'Chargement de vos discussions...',
          empty: 'Aucune discussion n’a encore été enregistrée.',
          untitled: 'Discussion {{shortId}}',
          noDocuments: 'Aucun fichier n’est rattaché à cette discussion.',
          deleteConfirm: 'Supprimer "{{conversationName}}" ainsi que ses fichiers joints ?',
          dialog: {
            empty: 'Aucun message n’a encore été enregistré pour cette discussion.',
            roles: {
              assistant: 'Assistant',
              user: 'Utilisateur',
              tool: 'Outil',
              system: 'Système'
            }
          }
        }
      },
    chat: {
      title: 'Clippy',
      titleExpanded: 'Clippy',
      collapse: 'Réduire la fenêtre de chat',
      expand: 'Agrandir la fenêtre de chat',
      close: 'Fermer le chat',
      open: 'Ouvrir le chat assistant',
      helpAction: 'Demander ce que je peux faire',
      conversations: {
        open: 'Afficher vos discussions',
        empty: 'Aucune discussion pour le moment',
        untitled: 'Discussion'
      },
      helpPrompt: 'Que puis-je te demander ?',
      suggestions: {
        weeklyPriorities: 'Résume mes priorités de la semaine.',
        accountsNeedingAttention: 'Quels comptes demandent mon attention en premier ?',
        followUpEmailDraft: 'Rédige un e-mail de suivi après un premier rendez-vous.'
      },
      composer: {
        message: 'Message',
        placeholder: 'Posez-moi une question.',
        footer: 'Interface uniquement pour le moment'
      },
      documents: {
        eyebrow: 'Fichiers de conversation',
        title: 'Documents joints',
        addAction: 'Ajouter des fichiers',
        uploading: 'Téléversement...',
        loading: 'Chargement des documents de la conversation...',
        empty: 'Aucun fichier n’a encore été joint à cette conversation.',
        downloadAction: 'Télécharger',
        downloading: 'Téléchargement...',
        deleteAction: 'Supprimer',
        deleteConfirm: 'Supprimer "{{fileName}}" de cette conversation ?'
      },
      notifications: {
        noSelectedServer: 'Aucun serveur LLM sélectionné',
        noAgentProfile: 'Aucun profil d\'agent LLM'
      }
    },
    allComponents: {
      hero: {
        eyebrow: 'Thème Material global',
        title: 'Palette sombre, accent vert, surfaces techniques.',
        copy: 'Tous les composants Material reposent maintenant sur les variables de `styles.scss` et sur une typographie monospace cohérente avec le style produit actuel.',
        summaryAria: 'Points clés du thème'
      },
      highlights: {
        dark: 'Sombre',
        monospace: 'Monospace',
        dense: 'Dense',
        highContrast: 'Fort contraste'
      },
      sampleNotes: 'Pipeline CRM dense, lisible et prêt pour un usage quotidien.',
      tabs: {
        form: 'Formulaire',
        actions: 'Actions'
      },
      form: {
        title: 'Contact principal',
        subtitle: 'Vérifier les champs, boutons et sélecteurs',
        company: 'Société',
        status: 'Statut',
        priority: 'Priorité',
        notes: 'Notes',
        hint: 'Le sous-texte et l\'état focus utilisent aussi le thème personnalisé.',
        nda: 'NDA signé',
        notifications: 'Alertes pipeline',
        preferredChannel: 'Canal préféré',
        cancel: 'Annuler',
        saveDraft: 'Enregistrer le brouillon',
        validate: 'Valider'
      },
      state: {
        title: 'État actuel',
        subtitle: 'Lecture rapide des tokens Material',
        notifications: 'Notifications',
        channel: 'Canal'
      },
      actions: {
        title: 'Variantes de boutons',
        text: 'Texte',
        outline: 'Contour',
        primary: 'Primaire',
        surface: 'Surfacique'
      }
    },
    errors: {
      groups: {
        load: 'Impossible de charger les groupes.',
        required: 'Le nom du groupe est obligatoire.',
        selectionRequired: 'Un groupe sélectionné est obligatoire.',
        create: 'Impossible de créer le groupe.',
        delete: 'Impossible de supprimer le groupe sélectionné.',
        addUser: 'Impossible d’ajouter l’utilisateur au groupe sélectionné.',
        missingPayload: 'Le groupe a été créé sans charge utile.'
      },
      companies: {
        load: 'Impossible de charger les entreprises.',
        detail: 'Impossible de charger les détails de l\'entreprise.',
        documentsTooLarge: 'Le document d’entreprise sélectionné dépasse la limite de 50 Mo.',
        documentsUpload: 'Impossible de téléverser le document sélectionné pour l’entreprise.',
        documentsDownload: 'Impossible de télécharger le document sélectionné pour l’entreprise.',
        documentsDelete: 'Impossible de supprimer le document sélectionné pour l’entreprise.',
        webLinksSave: 'Impossible d’enregistrer les liens web de l’entreprise.'
      },
      offerings: {
        load: 'Impossible de charger les offres.',
        documentsTooLarge: 'Le document d’offre sélectionné dépasse la limite de 50 Mo.',
        documentsLoad: 'Impossible de charger les documents de l’offre sélectionnée.',
        documentsUpload: 'Impossible de téléverser le document sélectionné pour l’offre.',
        documentsDownload: 'Impossible de télécharger le document sélectionné pour l’offre.',
        documentsDelete: 'Impossible de supprimer le document sélectionné pour l’offre.'
      },
      groupPortfolioAnalysis: {
        load: 'Impossible de charger l\'analyse portefeuille du groupe.',
        history: 'Impossible de charger l\'historique des analyses portefeuille du groupe.',
        generate: 'Impossible de générer une nouvelle analyse portefeuille du groupe.'
      },
      admin: {
        documentsLoad: 'Impossible de charger les fichiers de discussion de l’utilisateur sélectionné.',
        documentsDelete: 'Impossible de supprimer le fichier de discussion sélectionné pour cet utilisateur.',
        deletionPreviewLoad: 'Impossible de charger l’aperçu de suppression de l’utilisateur sélectionné.',
        deleteUser: 'Impossible de supprimer le profil utilisateur sélectionné.'
      },
      databaseAdmin: {
        loadBackups: 'Impossible de charger les backups de base de données.',
        createBackup: 'Impossible de créer un backup de la base de données.',
        downloadBackup: 'Impossible de télécharger le backup de la base de données sélectionné.',
        restoreBackup: 'Impossible de restaurer la base de données à partir du backup téléversé.'
      },
      profile: {
        load: 'Impossible de charger votre profil.',
        save: 'Impossible de sauvegarder votre profil.',
        documentsLoad: 'Impossible de charger vos documents publiés.',
        documentsDownload: 'Impossible de télécharger le document de discussion sélectionné.',
        documentsDelete: 'Impossible de supprimer le document de discussion sélectionné.',
        conversationsLoad: 'Impossible de charger vos discussions.',
        conversationsOpen: 'Impossible d’ouvrir la discussion sélectionnée.',
        conversationsDelete: 'Impossible de supprimer la discussion sélectionnée.'
      },
      chat: {
        documentsTooLarge: 'L’un des fichiers sélectionnés dépasse la limite de 50 Mo.',
        documentsUpload: 'Impossible de téléverser les fichiers sélectionnés pour la conversation.',
        documentsDownload: 'Impossible de télécharger le fichier sélectionné de la conversation.',
        documentsDelete: 'Impossible de supprimer le fichier sélectionné de la conversation.'
      }
    }
  },
  it: {
    app: {
      title: 'CRM Agentique',
      unauthenticated: {
        eyebrow: 'Trasformazione aziendale',
        prompt: 'Accedi prima di continuare.'
      },
      sessionExpired: {
        eyebrow: 'Sessione scaduta',
        title: 'Non sei piu connesso',
        prompt: 'Riconnettiti usando uno dei pulsanti di accesso qui sotto. Le bozze locali restano disponibili su questo dispositivo.'
      }
    },
    common: {
      languages: {
        english: 'Inglese',
        french: 'Francese',
        italian: 'Italiano'
      },
      themes: {
        light: 'Chiaro',
        dark: 'Scuro',
        ninon: 'Ninon'
      },
      labels: {
        assistant: 'Assistente',
        unavailable: 'Non disponibile',
        unknown: 'Sconosciuto',
        notSpecified: 'Non specificato',
        notTracked: 'Non tracciato',
        mainContact: 'Contatto principale',
        selected: 'Selezionato',
        selectedGroup: 'Gruppo selezionato',
        allGroups: 'Tutti i gruppi',
        noGroupAssigned: 'Nessun gruppo assegnato',
        noMainContact: 'Nessun contatto principale',
        unnamedCompany: 'Azienda senza nome',
        unnamedContact: 'Contatto senza nome',
        unnamedOffering: 'Offerta senza nome',
        locationUnavailable: 'Localizzazione non disponibile',
        revenueUnavailable: 'Fatturato non disponibile',
        websiteUnavailable: 'Sito web non disponibile',
        phoneUnavailable: 'Telefono non disponibile',
        linkedInUnavailable: 'LinkedIn non disponibile',
        unknownStatus: 'Stato sconosciuto',
        unknownSource: 'Origine sconosciuta',
        unknownRelationship: 'Relazione sconosciuta',
        unknownType: 'Tipo sconosciuto',
        unknownCompany: 'Azienda sconosciuta',
        unknownContact: 'Contatto sconosciuto',
        openEnded: 'Senza data di fine',
        startDateUnavailable: 'Data di inizio non disponibile',
        noEmailAddress: 'Nessun indirizzo e-mail',
        noActionScheduled: 'Nessuna azione pianificata',
        active: 'Attivo',
        disabled: 'Disattivato',
        available: 'Disponibile',
        missing: 'Mancante',
        draftRestored: 'Bozza locale ripristinata.',
        requiredField: '{{label}} *',
        withCount: '{{label}} ({{count}})'
      },
      actions: {
        addAction: 'Aggiungi azione',
        addComment: 'Aggiungi commento',
        addNote: 'Aggiungi nota',
        addCustomer: 'Aggiungi cliente',
        addContract: 'Aggiungi contratto',
        addOffering: 'Aggiungi offerta',
        admin: 'Admin',
        backToCompanies: 'Torna alle aziende',
        backToHome: 'Torna alla home',
        cancel: 'Annulla',
        close: 'Chiudi',
        createGroup: 'Crea gruppo',
        delete: 'Elimina',
        detail: 'Dettaglio',
        edit: 'Modifica',
        logout: 'Disconnetti',
        refresh: 'Aggiorna',
        refreshOfferings: 'Aggiorna offerte',
        saveAction: 'Salva azione',
        saveComment: 'Salva nota',
        saveContract: 'Salva contratto',
        saveCustomer: 'Salva cliente',
        send: 'Invia',
        setAsMainContact: 'Imposta come contatto principale',
        saving: 'Salvataggio...',
        viewDetails: 'Vedi dettagli'
      },
      confirm: {
        deleteAction: 'Eliminare questa azione dall’azienda?',
        deleteComment: 'Eliminare questo commento dall’azienda?',
        deleteNote: 'Eliminare questa nota dall’azienda?',
        deleteRex: 'Eliminare questo REX dall’azienda?',
        deleteCustomer: 'Eliminare questo cliente dall’azienda?',
        deleteContract: 'Eliminare questo contratto dall’azienda?',
        deleteOffering: 'Eliminare questa offerta dal catalogo?',
        deleteCompany: 'Eliminare "{{companyName}}" dal CRM? Tutti i clienti, contratti, azioni, note e documenti collegati verranno eliminati.'
      },
      counts: {
        companies: {
          one: '{{count}} azienda',
          other: '{{count}} aziende'
        },
        customers: {
          one: '{{count}} cliente',
          other: '{{count}} clienti'
        },
        contacts: {
          one: '{{count}} contatto',
          other: '{{count}} contatti'
        },
        activeCustomers: {
          one: '{{count}} cliente attivo',
          other: '{{count}} clienti attivi'
        },
        offerings: {
          one: '{{count}} offerta',
          other: '{{count}} offerte'
        },
        qualifiedCompanies: {
          one: '{{count}} azienda qualificata',
          other: '{{count}} aziende qualificate'
        },
        contracts: {
          one: '{{count}} contratto',
          other: '{{count}} contratti'
        },
        activeContracts: {
          one: '{{count}} contratto attivo',
          other: '{{count}} contratti attivi'
        },
        openActions: {
          one: '{{count}} azione aperta',
          other: '{{count}} azioni aperte'
        },
        actions: {
          one: '{{count}} azione',
          other: '{{count}} azioni'
        },
        comments: {
          one: '{{count}} commento',
          other: '{{count}} commenti'
        },
        notes: {
          one: '{{count}} nota',
          other: '{{count}} note'
        },
        messages: {
          one: '{{count}} messaggio',
          other: '{{count}} messaggi'
        },
        years: {
          one: '{{count}} anno',
          other: '{{count}} anni'
        },
        documents: {
          one: '{{count}} documento',
          other: '{{count}} documenti'
        },
        webLinks: {
          one: '{{count}} link web',
          other: '{{count}} link web'
        },
        rex: {
          one: '{{count}} REX',
          other: '{{count}} REX'
        },
        groups: {
          one: '{{count}} gruppo',
          other: '{{count}} gruppi'
        },
        members: {
          one: '{{count}} membro',
          other: '{{count}} membri'
        },
        admins: {
          one: '{{count}} admin',
          other: '{{count}} admin'
        },
        adminSlots: {
          one: '{{count}} slot admin',
          other: '{{count}} slot admin'
        },
        days: {
          one: '{{count}} giorno',
          other: '{{count}} giorni'
        }
      },
      enums: {
        companyStatus: {
          PROSPECT: 'Prospect',
          QUALIFIED: 'Qualificata',
          ACTIVE_CUSTOMER: 'Cliente attivo',
          PARTNER: 'Partner',
          LOST: 'Persa'
        },
        leadSource: {
          WEBSITE: 'Sito web',
          REFERRAL: 'Referral',
          OUTBOUND: 'Outbound',
          EVENT: 'Evento',
          LINKEDIN: 'LinkedIn',
          OTHER: 'Altro'
        },
        relationshipStatus: {
          PROSPECT: 'Prospect',
          CUSTOMER: 'Cliente',
          OLD_RELATIONSHIP: 'Relazione passata'
        },
        offeringType: {
          FIXED_PRICE: 'Prezzo fisso',
          TIME_AND_MATERIAL: 'Time and material'
        },
        noteType: {
          COMMENT: 'Commento',
          REFLECTION: 'Riflessione',
          OPPORTUNITY: 'Opportunità da approfondire'
        },
        actionType: {
          CALL: 'Chiamata',
          EMAIL: 'E-mail',
          MEETING: 'Riunione',
          FOLLOW_UP: 'Follow-up',
          TODO: 'Attività',
          LINKEDIN_OUTREACH: 'Contatto LinkedIn',
          PREPARE_PROPOSAL: 'Preparare proposta',
          SEND_PROPOSAL: 'Inviare proposta',
          NEGOTIATE_CONTRACT: 'Negoziare contratto',
          RENEWAL_CHECK: 'Verifica rinnovo',
          UPSELL_CHECK: 'Verifica upsell',
          INTERNAL_TASK: 'Attività interna'
        },
        actionStatus: {
          OPEN: 'Aperta',
          IN_PROGRESS: 'In corso',
          DONE: 'Completata',
          CANCELLED: 'Annullata',
          BLOCKED: 'Bloccata',
          SNOOZED: 'Posticipata',
          OVERDUE: 'In ritardo'
        },
        actionPriority: {
          LOW: 'Bassa',
          NORMAL: 'Normale',
          HIGH: 'Alta',
          URGENT: 'Urgente'
        },
        previewStatus: {
          NEW: 'Nuovo',
          QUALIFIED: 'Qualificato',
          FOLLOW_UP: 'Follow-up',
          CONVERTED: 'Convertito'
        },
        previewPriority: {
          LOW: 'Bassa',
          NORMAL: 'Normale',
          HIGH: 'Alta'
        },
        previewChannel: {
          email: 'E-mail',
          phone: 'Telefono',
          meet: 'Videochiamata'
        }
      }
    },
    mainMenu: {
      home: 'Home',
      loadingGroups: 'Caricamento gruppi...',
      noGroupsAvailable: 'Nessun gruppo disponibile',
      signInProviders: 'Provider di accesso',
      signInWithGoogle: 'Accedi con Google',
      signInWithMicrosoft: 'Accedi con Microsoft',
      styleGuide: 'Guida di stile',
      languageSelector: 'Selettore lingua',
      themeSelector: 'Selettore tema'
    },
    home: {
      tabs: {
        dashboard: 'Dashboard',
        groupPortfolio: 'Portafoglio clienti',
        groupMembers: 'Membri del gruppo',
        groupAnalysis: 'Analisi portafoglio',
        offerings: 'Offerte'
      },
      hero: {
        eyebrow: 'Cruscotto commerciale',
        title: 'Concentrati sugli account che richiedono azione adesso',
        copy: 'Parti dal carico di lavoro attivo, tieni d’occhio le opportunita qualificate e apri direttamente dalla home l’account che richiede attenzione.',
        summaryAria: 'Riepilogo del portafoglio aziende'
      },
      focus: {
        title: 'Account da trattare',
        subtitle: 'Un elenco breve di account ordinati per lavoro aperto, delivery attiva e potenziale commerciale.',
        loading: 'Caricamento account prioritari...',
        emptyTitle: 'Nessun account richiede attenzione in questo momento.',
        emptyDetail: 'Crea aziende o cambia gruppo per popolare questo elenco di priorita.',
        openCompanyAria: 'Apri {{companyName}}',
        summaryAria: 'Riepilogo account prioritari',
        priorityOpenActions: 'Follow-up in corso',
        priorityActiveContracts: 'Account attivo da proteggere',
        priorityQualified: 'Opportunita qualificata da spingere',
        priorityCoverage: 'Copertura da costruire'
      },
      actionTimeline: {
        title: 'Timeline delle azioni',
        subtitle: 'Le tue azioni aperte che richiedono attenzione ora, raggruppate per orizzonte di esecuzione.',
        loading: 'Caricamento timeline azioni...',
        emptyTitle: 'Nessuna azione urgente visibile al momento.',
        emptyDetail: 'Qui compariranno le azioni assegnate con scadenza in ritardo, oggi o questa settimana.',
        emptyBucket: 'Nessuna azione in questo orizzonte.',
        overdue: 'In ritardo',
        today: 'Oggi',
        thisWeek: 'Questa settimana',
        dueLabel: 'Scadenza {{date}}',
        scheduledLabel: 'Pianificata {{date}}',
        noDate: 'Nessuna scadenza',
        openAccount: 'Apri account'
      },
      opportunities: {
        title: 'Note opportunita',
        subtitle: 'Opportunita commerciali recenti registrate nel portafoglio account visibile.',
        loading: 'Caricamento note opportunita...',
        emptyTitle: 'Nessuna nota opportunita visibile al momento.',
        emptyDetail: 'Crea note di tipo opportunita dalle schede account per mostrarle qui.',
        companyWide: 'Nota a livello azienda',
        openOpportunityAria: 'Apri le note opportunita di {{companyName}}'
      },
      offerings: {
        title: 'Catalogo offerte',
        subtitle: 'Offerte commerciali indipendenti disponibili nel CRM.',
        toolbarEyebrow: 'Catalogo commerciale',
        toolbarCopy: 'Consulta il catalogo corrente, poi apri un’offerta in una dialog per crearla, aggiornarla o gestire i file allegati.',
        newHeading: 'Nuova offerta',
        createLabel: 'Crea offerta',
        detailHeading: 'Dettaglio offerta',
        editHeading: 'Modifica offerta',
        loading: 'Caricamento offerte...',
        loadingEditor: 'Caricamento dialog offerta...',
        loadingEditorDetail: 'Recupero degli ultimi dettagli dell’offerta dal CRM.',
        emptyTitle: 'Nessuna offerta disponibile.',
        emptyDetail: 'Crea un’offerta da questo catalogo, poi riaprila per allegare documenti.',
        missingDescription: 'Nessuna descrizione offerta disponibile.',
        workspaceTitle: 'Seleziona un’offerta per visualizzarla o modificarla.',
        workspaceDetail: 'L’offerta selezionata resta aperta qui mentre il catalogo rimane visibile.',
        actionFailed: 'L’azione sull’offerta non e riuscita.',
        loadError: 'Impossibile caricare i dettagli dell’offerta.',
        createError: 'Impossibile creare l’offerta.',
        updateError: 'Impossibile aggiornare l’offerta.',
        deleteError: 'Impossibile eliminare l’offerta.',
        refreshing: 'Aggiornamento...'
      },
      companies: {
        title: 'Portafoglio clienti',
        subtitle: 'Account cliente visibili con ricerca, filtri per stato e tag, e ordinamento per segnali commerciali.',
        toolbarEyebrow: 'Portafoglio clienti',
        toolbarCopy: 'Cerca nel portafoglio visibile, combina i filtri per stato e tag e ordina gli account per carico attivo, copertura, nome o ricavi da contratto.',
        filteredCopy: '{{count}} account cliente corrispondono ai filtri correnti.',
        loading: 'Caricamento aziende...',
        emptyTitle: 'Nessuna azienda disponibile.',
        emptyDetail: 'Crea aziende dagli strumenti CRM e aggiorna questa vista.',
        noResultsTitle: 'Nessun account cliente corrisponde ai filtri correnti.',
        noResultsDetail: 'Modifica il termine di ricerca, gli stati selezionati, i tag selezionati o l’ordinamento per ampliare il portafoglio.',
        searchPlaceholder: 'Cerca un cliente',
        statusFilterLabel: 'Stato',
        tagFilterLabel: 'Tag',
        sortLabel: 'Ordina per',
        sortPriority: 'Lavoro attivo e copertura',
        sortName: 'Nome',
        sortContractRevenue: 'Ricavi da contratto',
        industry: 'Settore',
        employees: 'Dipendenti',
        revenue: 'Fatturato',
        contractRevenue: 'Ricavi da contratto',
        source: 'Origine',
        tagsAria: 'Tag account cliente',
        openCompanyAria: 'Apri il dettaglio di {{companyName}}'
      },
      groupMembers: {
        title: 'Membri del gruppo',
        subtitle: '{{memberCount}} visibili nel gruppo selezionato, con l’attuale staffing sui contratti.',
        toolbarEyebrow: 'Competenze del team',
        toolbarCopy: 'Filtra il gruppo selezionato per tag competenze per concentrarti sulle persone giuste.',
        filteredCopy: '{{count}} membri corrispondono ai tag competenze selezionati.',
        tagFilterLabel: 'Tag competenze',
        loading: 'Caricamento membri del gruppo...',
        emptyTitle: 'Nessun membro del gruppo ancora visibile.',
        emptyDetail: 'Seleziona un gruppo con utenti per mostrare staffing e bench.',
        noResultsTitle: 'Nessun membro corrisponde ai tag competenze selezionati.',
        noResultsDetail: 'Modifica o svuota il filtro tag corrente per ampliare la lista dei membri.',
        benchTitle: 'Bench',
        benchSubtitle: 'Membri senza contratto attivo in questo momento.',
        staffedTitle: 'In assegnazione',
        staffedSubtitle: 'Membri attualmente assegnati su contratti attivi.',
        emptyBench: 'Nessuno e attualmente in bench.',
        emptyStaffed: 'Nessuna assegnazione attiva visibile in questo gruppo.',
        skillTags: 'Tag competenze',
        staffedCount: '{{count}} assegnati',
        benchCount: '{{count}} in bench',
        contractCount: '{{count}} contratto(i)',
        contractMeta: '{{companyName}} · {{contractLabel}}',
        contractDateRange: '{{startDate}} - {{endDate}}',
        contractUntil: 'Fino al {{endDate}}',
        contractFrom: 'Dal {{startDate}}',
        dialog: {
          openProfile: 'Apri il profilo di {{memberName}}',
          jobTitle: 'Ruolo',
          phone: 'Telefono',
          linkedInUrl: 'LinkedIn',
          totalExperience: 'Esperienza totale',
          currentRoleExperience: 'Esperienza nel ruolo attuale',
          coreSkills: 'Competenze principali',
          previousExperiences: 'Esperienze precedenti',
          upskillingGoals: 'Obiettivi di crescita competenze',
          activeContracts: 'Contratti attivi',
          noActiveContracts: 'Nessun contratto attivo è attualmente visibile per questo membro.',
          openContract: 'Apri contratto'
        }
      },
      groupAnalysis: {
        title: 'Analisi del portafoglio',
        subtitle: 'Storico delle analisi di portafoglio salvate per {{groupName}}.',
        toolbarEyebrow: 'Analisi di gruppo',
        toolbarCopy: 'Consulta l’ultima analisi generata dall’agente per il gruppo selezionato e sfoglia gli snapshot precedenti salvati nel CRM.',
        generate: 'Genera una nuova analisi',
        generating: 'Lavoro in corso',
        loading: 'Caricamento analisi del portafoglio...',
        emptyTitle: 'Nessuna analisi di portafoglio salvata è ancora disponibile.',
        emptyDetail: 'Chiedi all’agente di generare l’analisi del portafoglio del gruppo, poi aggiorna questa vista.',
        loadErrorTitle: 'Impossibile caricare l’analisi del portafoglio.',
        historyTitle: 'Storico',
        historySubtitle: 'Snapshot precedenti nella lingua corrente.',
        emptyHistory: 'Nessuna analisi precedente è ancora salvata.',
        sections: {
          analysisDate: 'Data dell’analisi',
          portfolioOverview: 'Vista d’insieme del portafoglio',
          sectorAnalysisAndTransformationSignals: 'Analisi settoriale e segnali di trasformazione',
          commercialMaturityEvaluation: 'Valutazione della maturità commerciale',
          priorityCommercialOpportunities: 'Opportunità commerciali prioritarie',
          portfolioGovernanceRecommendations: 'Raccomandazioni di governance del portafoglio',
          summary: 'Sintesi',
          immediateNextStep: 'Prossimo passo immediato (per te)'
        }
      }
    },
    companyDetail: {
      hero: {
        eyebrow: 'Scheda azienda',
        loadingTitle: 'Caricamento azienda...',
        loadingCopy: 'Recupero degli ultimi dettagli azienda dal CRM.',
        errorTitle: 'Impossibile caricare l’azienda',
        unavailableTitle: 'La pagina di dettaglio azienda non e disponibile in questo momento.',
        unavailableDetail: 'Torna alla directory e riprova.',
        deleteCompany: 'Elimina azienda dal CRM',
        deleting: 'Eliminazione...',
        deleteError: 'Impossibile eliminare l’azienda.',
        summaryAria: 'Riepilogo scheda azienda',
        missingDescription: 'Nessuna descrizione azienda disponibile.',
        location: 'Localizzazione',
        mainContact: 'Contatto principale',
        statRevenue: 'Fatturato annuo',
        statOpenActions: 'Azioni aperte',
        statActiveContracts: 'Contratti attivi',
        statTags: 'Tag account'
      },
      navigation: {
        ariaLabel: 'Sezioni dettaglio azienda',
        overview: 'Panoramica',
        strategic: 'Strategico',
        customers: 'Clienti',
        comments: 'Commenti',
        actions: 'Azioni',
        contracts: 'Contratti'
      },
      overview: {
        title: 'Panoramica',
        subtitle: 'Informazioni core dell’account e riferimenti collegati.',
        businessSnapshot: 'Snapshot business',
        relationshipRhythm: 'Ritmo della relazione',
        channels: 'Canali',
        industry: 'Settore',
        location: 'Localizzazione',
        employeeCount: 'Numero dipendenti',
        annualRevenue: 'Fatturato annuo',
        leadSource: 'Origine lead',
        ownerUser: 'Responsabile account',
        saveOwnerUser: 'Salva responsabile',
        ownerUpdateError: 'Impossibile aggiornare il responsabile dell’account.',
        ownerGroup: 'Gruppo proprietario',
        mainContact: 'Contatto principale',
        parentCompany: 'Societa madre',
        tags: 'Tag',
        tagsAria: 'Tag azienda',
        websiteAction: 'Apri sito web',
        linkedInAction: 'Apri LinkedIn'
      },
      communication: {
        title: 'Comunicazione',
        subtitle: 'Canali aziendali e timeline della relazione.',
        website: 'Sito web',
        linkedIn: 'LinkedIn',
        notes: 'Note'
      },
      strategic: {
        title: 'Vista strategica',
        subtitle: 'Segnali business e contesto commerciale di questo account.',
        emptyTitle: 'Nessuna informazione strategica registrata per questa azienda.',
        emptyDetail: 'Aggiorna strategia, notizie recenti e opportunita commerciali dagli strumenti CRM.',
        coverage: 'Copertura',
        currentStrategy: 'Strategia attuale',
        recentNews: 'Notizie recenti',
        commercialOpportunities: 'Opportunita commerciali',
        recommendedNextActions: 'Prossime azioni consigliate',
        missingCurrentStrategy: 'Nessuna strategia attuale registrata.',
        missingRecentNews: 'Nessuna notizia recente registrata.',
        missingCommercialOpportunities: 'Nessuna opportunita commerciale registrata.',
        missingRecommendedNextActions: 'Nessuna prossima azione consigliata registrata.'
      },
      customers: {
        title: 'Clienti',
        subtitle: 'Persone collegate a questo account aziendale.',
        kindBadge: 'Contatto',
        toolbarEyebrow: 'Mappa della relazione',
        toolbarCopy: 'Cerca i contatti dell’account e mantieni il profilo selezionato aperto in uno spazio dedicato.',
        filteredCopy: '{{count}} contatti corrispondono ai filtri correnti.',
        searchPlaceholder: 'Cerca per nome, ruolo, e-mail o owner',
        filterAll: 'Tutti i contatti',
        filterMain: 'Contatto principale',
        filterAssigned: 'Assegnati',
        filterUnassigned: 'Non assegnati',
        actionFailed: 'L’azione cliente non e riuscita.',
        newHeading: 'Nuovo cliente',
        createLabel: 'Crea cliente',
        emptyTitle: 'Nessun cliente collegato a questa azienda.',
        emptyDetail: 'Aggiungi schede cliente collegate a questa azienda per popolare questa sezione.',
        noResultsTitle: 'Nessun contatto corrisponde ai filtri correnti.',
        noResultsDetail: 'Modifica la ricerca o i filtri per ampliare il risultato.',
        missingJobTitle: 'Ruolo non disponibile',
        loadingEditor: 'Caricamento editor cliente...',
        loadingEditorDetail: 'Recupero degli ultimi dettagli cliente dal CRM.',
        detailHeading: 'Dettaglio cliente',
        editHeading: 'Modifica cliente',
        workspaceTitle: 'Seleziona un contatto per visualizzarlo o modificarlo dallo spazio fissato.',
        workspaceDetail: 'Il contatto selezionato resta aperto qui mentre l’elenco rimane visibile.',
        loadError: 'Impossibile caricare i dettagli del cliente.',
        createError: 'Impossibile creare il cliente.',
        updateError: 'Impossibile aggiornare il cliente.',
        deleteError: 'Impossibile eliminare il cliente.',
        mainContactError: 'Impossibile aggiornare il contatto principale.',
        noRelationshipOwner: 'Nessun owner della relazione',
        linkedInAction: 'Apri LinkedIn'
      },
      contracts: {
        title: 'Contratti',
        subtitle: 'Engagement commerciali collegati a questa azienda.',
        kindBadge: 'Contratto',
        toolbarEyebrow: 'Portafoglio contratti',
        toolbarCopy: 'Consulta gli engagement attivi, futuri e conclusi senza lasciare la scheda azienda.',
        filteredCopy: '{{count}} contratti corrispondono ai filtri correnti.',
        searchPlaceholder: 'Cerca per cliente o offerta',
        filterAll: 'Tutti i contratti',
        filterActive: 'Attivi',
        filterUpcoming: 'Futuri',
        filterEnded: 'Conclusi',
        actionFailed: 'L’azione sul contratto non e riuscita.',
        newHeading: 'Nuovo contratto',
        createLabel: 'Crea contratto',
        emptyTitle: 'Nessun contratto collegato a questa azienda.',
        emptyDetail: 'I contratti collegati nel CRM appariranno qui.',
        noResultsTitle: 'Nessun contratto corrisponde ai filtri correnti.',
        noResultsDetail: 'Allarga la ricerca o cambia filtro di ciclo di vita.',
        dailyRate: 'Tariffa giornaliera',
        duration: 'Durata',
        endDate: 'Data di fine',
        customer: 'Cliente',
        loadingEditor: 'Caricamento editor contratto...',
        loadingEditorDetail: 'Recupero degli ultimi dettagli contratto dal CRM.',
        detailHeading: 'Dettaglio contratto',
        editHeading: 'Modifica contratto',
        workspaceTitle: 'Seleziona un contratto per analizzare i dettagli commerciali affiancati.',
        workspaceDetail: 'Lo spazio del contratto resta fissato mentre navighi nel portafoglio.',
        lifecycle: {
          active: 'Attivo',
          upcoming: 'Futuro',
          ended: 'Concluso'
        },
        loadError: 'Impossibile caricare i dettagli del contratto.',
        createError: 'Impossibile creare il contratto.',
        updateError: 'Impossibile aggiornare il contratto.',
        deleteError: 'Impossibile eliminare il contratto.'
      },
      rex: {
        title: 'REX',
        subtitle: 'Feedback di esperienza strutturati collegati ai contratti.',
        toolbarEyebrow: 'Feedback di esperienza',
        toolbarCopy: 'Capitalizza sfide, soluzioni e valore consegnato per engagement attivi o conclusi.',
        filteredCopy: '{{count}} REX corrispondono ai filtri correnti.',
        searchPlaceholder: 'Cerca per titolo, contratto, consulente o contenuto',
        addAction: 'Aggiungi REX',
        actionFailed: 'L’operazione sul REX non e riuscita.',
        newHeading: 'Nuovo REX',
        detailHeading: 'Dettaglio REX',
        editHeading: 'Modifica REX',
        createLabel: 'Crea REX',
        saveLabel: 'Salva REX',
        emptyTitle: 'Nessun REX e ancora collegato a questa azienda.',
        emptyDetail: 'Aggiungi feedback strutturati dai contratti per conservare esperienza riutilizzabile nel CRM.',
        noContractsTitle: 'Nessun contratto disponibile per i REX.',
        noContractsDetail: 'Crea un contratto prima di aggiungere feedback di esperienza.',
        noResultsTitle: 'Nessun REX corrisponde ai filtri correnti.',
        noResultsDetail: 'Allarga la ricerca per mostrare piu feedback di esperienza.',
        unknownContract: 'Contratto sconosciuto',
        untitled: 'REX senza titolo',
        noConsultants: 'Nessun consulente elencato',
        emptySection: 'Nessun contenuto registrato.',
        loadingEditor: 'Caricamento editor REX...',
        loadingEditorDetail: 'Recupero degli ultimi dettagli REX dal CRM.',
        loadError: 'Impossibile caricare i dettagli del REX.',
        createError: 'Impossibile creare il REX.',
        updateError: 'Impossibile aggiornare il REX.',
        deleteError: 'Impossibile eliminare il REX.',
        fields: {
          contract: 'Contratto',
          title: 'Titolo',
          description: 'Descrizione',
          challenges: 'CHALLENGES',
          solution: 'SOLUTION',
          resultsAndValueAdded: 'RESULTS AND VALUE ADDED'
        }
      },
      actions: {
        title: 'Azioni',
        subtitle: 'Prossimi passi, follow-up e attivita interne collegate a questo account.',
        kindBadge: 'Azione',
        toolbarEyebrow: 'Board di esecuzione',
        toolbarCopy: 'Monitora cio che avanza, e bloccato o in ritardo direttamente dalla vista account.',
        filteredCopy: '{{count}} azioni corrispondono ai filtri correnti.',
        searchPlaceholder: 'Cerca per titolo, owner, cliente o offerta',
        filterAll: 'Tutte le azioni',
        filterOpen: 'Aperte',
        filterOverdue: 'In ritardo',
        filterDone: 'Completate',
        actionFailed: 'L’operazione sull’azione non e riuscita.',
        newHeading: 'Nuova azione',
        createLabel: 'Crea azione',
        emptyTitle: 'Nessuna azione collegata a questa azienda.',
        emptyDetail: 'Traccia follow-up, meeting e lavoro interno da questo pannello.',
        noResultsTitle: 'Nessuna azione corrisponde ai filtri correnti.',
        noResultsDetail: 'Allarga la ricerca o cambia bucket di stato.',
        missingTitle: 'Azione senza titolo',
        noDueDate: 'Nessuna scadenza',
        loadingEditor: 'Caricamento editor azione...',
        loadingEditorDetail: 'Recupero degli ultimi dettagli azione dal CRM.',
        detailHeading: 'Dettaglio azione',
        editHeading: 'Modifica azione',
        workspaceTitle: 'Seleziona un’azione per rivedere il contesto di esecuzione e aggiornarla.',
        workspaceDetail: 'Lo spazio di lavoro dell’azione resta visibile mentre navighi nell’elenco.',
        loadError: 'Impossibile caricare i dettagli dell’azione.',
        createError: 'Impossibile creare l’azione.',
        updateError: 'Impossibile aggiornare l’azione.',
        deleteError: 'Impossibile eliminare l’azione.'
      },
      comments: {
        title: 'Note',
        subtitle: 'Note tipizzate e file di contesto collegati a questo account nel tempo.',
        toolbarEyebrow: 'Note account',
        toolbarCopy: 'Registra interazioni, riflessioni e opportunita direttamente dalla pagina azienda.',
        filteredCopy: '{{count}} note corrispondono ai filtri correnti.',
        searchPlaceholder: 'Cerca per contenuto, tipo o autore',
        filterAll: 'Tutte le note',
        actionFailed: 'L’operazione sulla nota non e riuscita.',
        newHeading: 'Nuova nota',
        createLabel: 'Crea nota',
        emptyTitle: 'Nessuna nota collegata a questa azienda.',
        emptyDetail: 'Aggiungi note per costruire lo storico della relazione e far emergere i segnali commerciali.',
        noResultsTitle: 'Nessuna nota corrisponde ai filtri correnti.',
        noResultsDetail: 'Modifica i termini di ricerca per ampliare il risultato.',
        emptyEntry: 'Nessun contenuto nota registrato.',
        loadingEditor: 'Caricamento editor nota...',
        loadingEditorDetail: 'Recupero degli ultimi dettagli nota dal CRM.',
        detailHeading: 'Dettaglio nota',
        editHeading: 'Modifica nota',
        loadError: 'Impossibile caricare i dettagli della nota.',
        createError: 'Impossibile creare la nota.',
        updateError: 'Impossibile aggiornare la nota.',
        deleteError: 'Impossibile eliminare la nota.',
        documentsUploadError: 'Impossibile caricare il documento della nota.',
        documentsDownloadError: 'Impossibile scaricare il documento della nota.',
        documentsDeleteError: 'Impossibile eliminare il documento della nota.'
      },
      documents: {
        title: 'Documenti',
        subtitle: 'File allegati a questo account e preparati per un futuro utilizzo da parte del LLM.',
        toolbarEyebrow: 'Archivio documenti',
        toolbarCopy: '{{count}} documento(i) sono attualmente collegati a questa azienda.',
        toolbarEmpty: 'Carica qui i documenti dell’account cosi potranno essere esplorati in seguito dagli strumenti CRM e dagli agenti.',
        maxFileSize: 'Dimensione massima: 50 MB per file.',
        selectFile: 'Seleziona un file',
        upload: 'Carica documento',
        uploading: 'Caricamento...',
        emptyTitle: 'Nessun documento e ancora collegato a questa azienda.',
        emptyDetail: 'Carica PDF, documenti Word, PowerPoint o Excel per conservare il contesto dell’account nel CRM.',
        errorTitle: 'Operazione documento non riuscita.',
        download: 'Scarica',
        deleteConfirm: 'Eliminare {{fileName}} da questa azienda?',
        status: {
          pending: 'Estrazione in attesa',
          completed: 'Testo estratto',
          failed: 'Estrazione non riuscita'
        },
        webLinks: {
          eyebrow: 'Link esterni',
          copy: 'Riferisci siti o documenti utili ospitati fuori dal CRM.',
          label: 'Etichetta link',
          url: 'URL',
          urlPlaceholder: 'https://example.com/risorsa',
          description: 'Descrizione',
          addAction: 'Aggiungi link',
          empty: 'Nessun link web esterno e ancora collegato a questa azienda.',
          unnamed: 'Link esterno',
          deleteConfirm: 'Rimuovere {{label}} da questa azienda?'
        }
      }
    },
    commentDetail: {
      eyebrow: 'Editor nota',
      headings: {
        details: 'Dettaglio nota'
      },
      sections: {
        entry: 'Contenuto nota',
        entryCopy: 'Cattura l’interazione, la riflessione o l’opportunita da conservare nello storico dell’account.'
      },
      fields: {
        type: 'Tipo',
        customer: 'Cliente collegato',
        contract: 'Contratto collegato',
        author: 'Autore',
        createdAt: 'Creato il',
        updatedAt: 'Aggiornato il',
        updatedBy: 'Aggiornato da',
        content: 'Nota'
      },
      placeholders: {
        content: 'Scrivi il riepilogo dell’interazione, la riflessione, l’opportunita o il contesto relazionale da conservare nello storico dell’azienda.'
      },
      hints: {
        edited: 'Questa nota e stata aggiornata dopo la creazione iniziale.'
      },
      empty: {
        content: 'Nessun contenuto nota registrato.'
      },
      customer: {
        companyWide: 'Nota a livello azienda'
      },
      contract: {
        none: 'Nessun contratto collegato'
      },
      documents: {
        eyebrow: 'Documenti allegati',
        createHint: 'Salva prima la nota, poi allega i documenti di contesto.',
        editCopy: 'Allega direttamente a questa nota i file di contesto.',
        readOnlyCopy: 'Scarica i file di contesto allegati a questa nota.',
        selectFile: 'Seleziona un file',
        upload: 'Carica documento',
        uploading: 'Caricamento...',
        empty: 'Nessun documento e ancora allegato a questa nota.',
        deleteConfirm: 'Eliminare {{fileName}} da questa nota?'
      }
    },
    actionDetail: {
      eyebrow: 'Editor azione',
      headings: {
        details: 'Dettaglio azione'
      },
      sections: {
        core: 'Configurazione principale',
        coreCopy: 'Definisci cosa deve accadere e chi ne e responsabile.',
        relationships: 'Collegamenti commerciali',
        relationshipsCopy: 'Collega l’azione al cliente, contratto e offerta corretti.',
        planning: 'Pianificazione',
        planningCopy: 'Registra la tempistica prevista per esecuzione e completamento.',
        context: 'Contesto e risultato',
        contextCopy: 'Documenta brief, risultato atteso e risultato effettivo.'
      },
      fields: {
        title: 'Titolo',
        description: 'Descrizione',
        type: 'Tipo',
        status: 'Stato',
        priority: 'Priorita',
        dueDate: 'Data di scadenza',
        dueTime: 'Ora di scadenza',
        scheduledAt: 'Pianificata per',
        scheduledTime: 'Ora pianificata',
        completedAt: 'Completata il',
        completedTime: 'Ora di completamento',
        ownerUserId: 'Responsabile',
        customer: 'Cliente',
        contract: 'Contratto',
        offering: 'Offerta',
        outcome: 'Risultato',
        reminderEnabled: 'Promemoria attivo'
      },
      placeholders: {
        title: 'Follow-up dopo la revisione della proposta',
        description: 'Descrivi il prossimo passo atteso e il contesto per il team.',
        outcome: 'Registra il risultato una volta completata l’azione.'
      },
      hints: {
        lockedByContract: 'Cliente e offerta sono bloccati perche ereditati dal contratto selezionato.'
      },
      empty: {
        description: 'Nessuna descrizione registrata.',
        outcome: 'Nessun risultato registrato.'
      }
    },
    customerDetail: {
      eyebrow: 'Editor cliente',
      headings: {
        details: 'Dettaglio cliente'
      },
      sections: {
        identity: 'Identita e canali',
        identityCopy: 'Raccogli l’identita principale e i canali di contatto diretti.',
        relationship: 'Ownership della relazione',
        relationshipCopy: 'Chiarisci il livello di maturita della relazione e chi la gestisce.',
        context: 'Note di contesto',
        contextCopy: 'Documenta motivazioni, rischi e contesto della relazione per il team.'
      },
      fields: {
        firstName: 'Nome',
        lastName: 'Cognome',
        jobTitle: 'Ruolo',
        phoneNumber: 'Numero di telefono',
        email: 'E-mail',
        linkedInUrl: 'URL LinkedIn',
        relationshipStatus: 'Stato relazione',
        inRelationshipWith: 'In relazione con',
        keyInterests: 'Interessi chiave',
        keyConcerns: 'Preoccupazioni chiave',
        comments: 'Commenti'
      },
      placeholders: {
        firstName: 'Morgan',
        lastName: 'Lee',
        jobTitle: 'Responsabile Partnership',
        phoneNumber: '+39 333 12 34 567',
        email: 'morgan.lee@example.com',
        linkedInUrl: 'https://www.linkedin.com/in/example',
        keyInterests: 'Temi, prodotti e priorita importanti per questo cliente.',
        keyConcerns: 'Rischi, blocchi e obiezioni da tenere presenti.',
        comments: 'Note di relazione, contesto e interazioni recenti.'
      },
      empty: {
        keyInterests: 'Nessun interesse chiave registrato.',
        keyConcerns: 'Nessuna preoccupazione chiave registrata.',
        comments: 'Nessun commento registrato.'
      },
      linkedNotes: {
        title: 'Note collegate',
        copy: 'Consulta le note CRM esplicitamente collegate a questo cliente.',
        empty: 'Nessuna nota è ancora collegata a questo cliente.',
        openAction: 'Vedi nota',
        editAction: 'Modifica nota'
      }
    },
    offeringDetail: {
      eyebrow: 'Editor offerta',
      headings: {
        new: 'Nuova offerta',
        details: 'Dettaglio offerta'
      },
      fields: {
        name: 'Nome',
        offeringType: 'Tipo di offerta',
        description: 'Descrizione'
      },
      placeholders: {
        name: 'Workshop di discovery',
        description: 'Spiega cosa offre questa proposta e quando dovrebbe essere presentata.'
      },
      actions: {
        create: 'Crea offerta',
        save: 'Salva offerta'
      },
      documents: {
        eyebrow: 'Documenti offerta',
        title: 'Documenti allegati',
        toolbarCopy: '{{count}} documento/i sono attualmente collegati a questa offerta.',
        toolbarEmpty: 'Carica qui il materiale dell’offerta cosi resta disponibile nel flusso documentale del CRM.',
        maxFileSize: 'Dimensione massima: 50 MB per file.',
        selectFile: 'Seleziona un file',
        upload: 'Carica documento',
        uploading: 'Caricamento...',
        loading: 'Caricamento documenti...',
        emptyTitle: 'Nessun documento e ancora collegato a questa offerta.',
        emptyDetail: 'Carica PDF, file Word, PowerPoint o Excel collegati a questa offerta.',
        errorTitle: 'Operazione sul documento non riuscita.',
        download: 'Scarica',
        deleteConfirm: 'Eliminare {{fileName}} da questa offerta?',
        status: {
          pending: 'Estrazione in attesa',
          completed: 'Testo estratto',
          failed: 'Estrazione fallita'
        }
      },
      empty: {
        description: 'Nessuna descrizione offerta registrata.'
      }
    },
    contractDetail: {
      eyebrow: 'Editor contratto',
      headings: {
        details: 'Dettaglio contratto'
      },
      sections: {
        scope: 'Ambito',
        scopeCopy: 'Collega l’engagement al cliente e all’offerta corretti.',
        timeline: 'Timeline',
        timelineCopy: 'Mantieni date e durata allineate con l’engagement reale.',
        commercials: 'Dimensione commerciale',
        commercialsCopy: 'Monitora valore complessivo ed economia giornaliera.',
        staffing: 'Staffing',
        staffingCopy: 'Assegna uno o piu consulenti del gruppo o registra consulenti esterni nominativi.'
      },
      fields: {
        customer: 'Cliente',
        offering: 'Offerta',
        startDate: 'Data di inizio',
        endDate: 'Data di fine',
        durationInDays: 'Durata in giorni',
        totalInvoice: 'Fatturazione totale',
        dailyRate: 'Tariffa giornaliera',
        consultants: 'Consulenti',
        groupConsultant: 'Consulente del gruppo',
        externalConsultant: 'Consulente esterno'
      },
      actions: {
        addGroupConsultant: 'Aggiungi consulente del gruppo',
        addExternalConsultant: 'Aggiungi consulente esterno'
      },
      consultants: {
        groupMember: 'Membro del gruppo',
        external: 'Esterno',
        selectGroupMember: 'Seleziona un membro del gruppo',
        empty: 'Nessun consulente ancora assegnato a questo contratto.'
      },
      lifecycle: {
        active: 'Attivo',
        upcoming: 'Futuro',
        ended: 'Concluso'
      },
      linkedNotes: {
        title: 'Note collegate',
        copy: 'Consulta le note CRM esplicitamente collegate a questo contratto.',
        empty: 'Nessuna nota è ancora collegata a questo contratto.',
        openAction: 'Vedi nota',
        editAction: 'Modifica nota'
      }
    },
    admin: {
      hero: {
        eyebrow: 'Amministrazione',
        title: 'Gestione gruppi utenti',
        copy: 'Rivedi i gruppi a cui puoi accedere, creane di nuovi dal gruppo ADMIN e gestisci i membri dove sei admin.',
        summaryAria: 'Riepilogo pagina admin'
      },
      access: {
        title: 'L’accesso admin non e disponibile',
        subtitle: 'Devi amministrare almeno un gruppo per usare questo spazio.',
        detail: 'Entra nel gruppo ADMIN per creare nuovi gruppi o diventa admin di un gruppo esistente per gestirne i membri.'
      },
      create: {
        title: 'Crea un gruppo',
        subtitle: 'Provisiona un nuovo gruppo per permessi CRM e collaborazione.',
        groupName: 'Nome del gruppo',
        placeholder: 'Esempio: Sales Operations',
        currentDirectory: 'Directory corrente',
        adminCoverage: 'Copertura admin',
        manageableGroups: 'Gruppi gestibili',
        restricted: 'Solo i membri del gruppo ADMIN possono creare nuovi gruppi.',
        creating: 'Creazione...'
      },
      delete: {
        title: 'Elimina un gruppo',
        subtitle: 'Rimuovi un gruppo inutilizzato quando hai accesso di sistema ADMIN.',
        group: 'Gruppo da eliminare',
        hint: 'Il gruppo di sistema ADMIN non puo essere eliminato. I gruppi che possiedono ancora aziende sono bloccati lato backend.',
        empty: 'Nessun gruppo eliminabile disponibile al momento.',
        action: 'Elimina gruppo',
        deleting: 'Eliminazione...',
        confirm: 'Eliminare il gruppo "{{groupName}}"? Questa azione e irreversibile.'
      },
      membership: {
        title: 'Aggiungi un utente a un gruppo',
        subtitle: 'Aggiungi un membro o un admin a uno dei gruppi che gestisci.',
        editTitle: 'Modifica un utente visibile',
        editSubtitle: 'Aggiorna il profilo e il ruolo dell’utente selezionato in {{groupName}}.',
        empty: 'Non sei ancora admin di alcun gruppo.',
        group: 'Gruppo di destinazione',
        userId: 'ID utente OAuth',
        userIdPlaceholder: 'provider|user-id',
        role: 'Ruolo',
        memberRole: 'Membro',
        adminRole: 'Admin',
        firstName: 'Nome',
        lastName: 'Cognome',
        email: 'E-mail',
        phone: 'Telefono',
        linkedInUrl: 'URL LinkedIn',
        linkedInUrlPlaceholder: 'https://www.linkedin.com/in/example',
        experienceStartDate: 'Data di inizio esperienza',
        jobTitle: 'Ruolo',
        currentRoleStartDate: 'Data di inizio nel ruolo',
        coreSkills: 'Competenze principali',
        previousExperiences: 'Esperienze precedenti',
        upskillingGoals: 'Obiettivi di crescita competenze',
        skillTagsPreview: 'Anteprima tag competenze',
        skillTagsHint: 'Usa virgole, punti e virgola o ritorni a capo nel campo competenze per gestire i tag.',
        dialogHint: 'Usa la dialog dedicata per aggiungere un utente, poi il pulsante Edit nelle schede directory per aggiornare un profilo esistente.',
        addAction: 'Aggiungi utente',
        saveAction: 'Salva utente',
        cancelEdit: 'Annulla modifica',
        editLockedHint: 'Gruppo e ID utente OAuth restano bloccati durante la modifica di un utente esistente.',
        saving: 'Salvataggio...'
      },
      directory: {
        title: 'Gruppi gestiti',
        subtitle: 'Gruppi per i quali hai attualmente diritti di amministrazione.',
        loading: 'Caricamento gruppi...',
        emptyTitle: 'Nessun gruppo gestito disponibile.',
        emptyDetail: 'Crea un gruppo tramite accesso ADMIN o chiedi di essere aggiunto come admin a un gruppo esistente.',
        adminGroup: 'Gruppo admin',
        members: 'Membri',
        admins: 'Admin',
        fileStorage: 'Spazio: {{size}}',
        documentsAction: 'File',
        deleteAction: 'Elimina utente',
        noAdmins: 'Nessun admin registrato in questo gruppo.',
        noMembers: 'Nessun membro registrato in questo gruppo.',
        documentsDialog: {
          title: 'File conversazione di {{userName}}',
          loading: 'Caricamento dei file di conversazione...',
          empty: 'Nessun file di conversazione attualmente archiviato per questo utente.',
          deleteConfirm: 'Eliminare "{{fileName}}" dallo storico conversazioni di questo utente?',
          untitledConversation: 'Conversazione {{shortId}}'
        },
        deleteDialog: {
          title: 'Elimina {{userName}}',
          loading: 'Caricamento impatto eliminazione...',
          intro: 'Questa azione rimuove il profilo CRM, tutte le appartenenze ai gruppi, le conversazioni salvate e i file allegati di questo utente.',
          summary: 'Verranno eliminate {{groups}} appartenenze ai gruppi, {{conversations}} conversazioni e {{documents}} documenti.',
          confirmCopy: 'Eliminare questo profilo utente e tutto il contenuto elencato? L’azione non è reversibile.',
          blockedTitle: 'L’eliminazione è attualmente bloccata.',
          blockedSelf: 'Non puoi eliminare il tuo profilo utente.',
          blockedMissingAccess: 'Questo utente appartiene ancora a gruppi che non amministri.',
          blockedLastAdmin: '"{{groupName}}" rimarrebbe senza amministratore.',
          groupsTitle: 'Appartenenze ai gruppi',
          groupsEmpty: 'Nessuna appartenenza a gruppi è attualmente registrata per questo utente.',
          conversationsTitle: 'Conversazioni',
          conversationsEmpty: 'Nessuna conversazione salvata verrà eliminata.',
          conversationMeta: '{{messages}} messaggi · {{documents}} documenti',
          standaloneDocumentsTitle: 'File autonomi',
          standaloneDocumentsEmpty: 'Nessun file autonomo verrà eliminato.',
          untitledConversation: 'Conversazione {{shortId}}',
          groupRoleAdmin: 'Admin',
          groupRoleMember: 'Membro',
          groupAdminGroup: 'Gruppo di sistema',
          groupNotManaged: 'Non amministrato da te',
          groupLastAdmin: 'Ultimo admin',
          close: 'Annulla',
          action: 'Elimina utente',
          deleting: 'Eliminazione...'
        }
      },
      databaseMaintenance: {
        title: 'Manutenzione database',
        subtitle: 'I backup giornalieri sono conservati sul server. Solo i membri del gruppo ADMIN possono avviare un backup o ripristinare il database.',
        backupsTitle: 'Backup disponibili',
        backupsCopy: 'File di backup recenti conservati dal server.',
        loading: 'Caricamento backup...',
        empty: 'Nessun file di backup disponibile.',
        downloadAction: 'Scarica',
        createBackup: 'Crea backup adesso',
        creatingBackup: 'Creazione backup...',
        restoreTitle: 'Ripristina database',
        restoreCopy: 'Carica un file di backup per sostituire il database corrente. Prima del ripristino viene creato automaticamente un backup di sicurezza.',
        restoreConfirm: 'Ripristinare l’intero database dal file selezionato? I dati correnti verranno sostituiti.',
        restoreFileLabel: 'File di backup',
        restoreSelectedFile: 'File selezionato: {{fileName}}',
        restoreHint: 'File accettati: .gz o .json.gz generati dal servizio di backup CRM.',
        restoreAction: 'Ripristina database',
        restoring: 'Ripristino...',
        restoreSuccess: 'Ripristino completato da {{fileName}}.',
        restoreSummary: '{{collections}} collezioni e {{documents}} documenti ripristinati.',
        preRestoreBackup: 'Backup di sicurezza creato prima del ripristino: {{fileName}}',
        lastBackupCreated: 'Backup creato: {{fileName}}',
        triggers: {
          manual: 'Manuale',
          scheduled: 'Pianificato',
          pre_restore: 'Pre-ripristino'
        }
      }
    },
    userDetail: {
      hero: {
        eyebrow: 'Utente CRM',
        loadingTitle: 'Caricamento profilo utente',
        loadingCopy: 'Il profilo CRM e in caricamento.',
        errorTitle: 'Profilo utente non disponibile',
        unavailableTitle: 'Profilo utente non disponibile',
        unavailableDetail: 'Questo utente non e disponibile con la tua visibilita CRM attuale.'
      }
    },
    profile: {
      title: 'Profilo',
      hero: {
        eyebrow: 'Profilo personale',
        title: 'Il tuo profilo',
        copy: 'Aggiorna le informazioni personali associate al tuo account OAuth.',
        skillTags: 'Tag competenze'
      },
      form: {
        title: 'Dettagli profilo',
        subtitle: 'Questi campi sono memorizzati nel CRM e riutilizzati nelle schermate di amministrazione.',
        loading: 'Caricamento del tuo profilo...',
        firstName: 'Nome',
        lastName: 'Cognome',
        email: 'E-mail',
        phone: 'Telefono',
        linkedInUrl: 'URL LinkedIn',
        linkedInUrlPlaceholder: 'https://www.linkedin.com/in/example',
        experienceStartDate: 'Data di inizio esperienza',
        jobTitle: 'Ruolo',
        currentRoleStartDate: 'Data di inizio nel ruolo',
        coreSkills: 'Competenze principali',
        previousExperiences: 'Esperienze precedenti',
        upskillingGoals: 'Obiettivi di crescita competenze',
        tags: 'Tag competenze',
        tagsHint: 'Aggiungi o rimuovi i tag che descrivono meglio il tuo profilo competenze attuale.',
        tagsAddLabel: 'Aggiungi un tag',
        tagsPlaceholder: 'Esempio: sales',
        tagsAddAction: 'Aggiungi tag',
        tagsEmpty: 'Nessun tag competenze attualmente definito.',
        skillTagsPreview: 'Anteprima tag competenze',
        skillTagsHint: 'Usa virgole, punti e virgola o ritorni a capo nel campo competenze per gestire i tag.',
        language: 'Lingua',
        theme: 'Tema',
        save: 'Salva profilo',
        saveSuccess: 'Profilo salvato.',
        saving: 'Salvataggio...'
      },
        documents: {
          title: 'Documenti pubblicati',
          subtitle: 'Tutti i documenti caricati nelle tue conversazioni chat.',
          loading: 'Caricamento dei tuoi documenti...',
          empty: 'Nessun documento caricato finora.',
          deleteConfirm: 'Eliminare "{{fileName}}" dai tuoi file di conversazione?',
          downloadAction: 'Scarica',
          downloading: 'Download...'
        },
        conversations: {
          title: 'Conversazioni',
          subtitle: 'Consulta le tue conversazioni chat recenti, rileggi i messaggi ed elimina quelle che non ti servono piu.',
          loading: 'Caricamento delle tue conversazioni...',
          empty: 'Nessuna conversazione salvata finora.',
          untitled: 'Conversazione {{shortId}}',
          noDocuments: 'Nessun file allegato a questa conversazione.',
          deleteConfirm: 'Eliminare "{{conversationName}}" e i relativi file allegati?',
          dialog: {
            empty: 'Nessun messaggio e stato ancora salvato per questa conversazione.',
            roles: {
              assistant: 'Assistente',
              user: 'Utente',
              tool: 'Strumento',
              system: 'Sistema'
            }
          }
        }
      },
    chat: {
      title: 'Clippy',
      titleExpanded: 'Clippy',
      collapse: 'Riduci finestra chat',
      expand: 'Espandi finestra chat',
      close: 'Chiudi chat',
      open: 'Apri chat assistente',
      helpAction: 'Chiedi cosa posso fare',
      conversations: {
        open: 'Apri le tue conversazioni',
        empty: 'Nessuna conversazione per ora',
        untitled: 'Conversazione'
      },
      helpPrompt: 'Cosa posso chiederti?',
      suggestions: {
        weeklyPriorities: 'Riassumi le mie priorita per questa settimana.',
        accountsNeedingAttention: 'Quali account richiedono prima la mia attenzione?',
        followUpEmailDraft: 'Scrivi una e-mail di follow-up dopo un primo incontro.'
      },
      composer: {
        message: 'Messaggio',
        placeholder: 'Scrivi qui. [Invio] per inviare, [Shift+Invio] per una nuova riga.',
        footer: 'Solo interfaccia per ora'
      },
      documents: {
        eyebrow: 'File conversazione',
        title: 'Documenti allegati',
        addAction: 'Aggiungi file',
        uploading: 'Caricamento...',
        loading: 'Caricamento documenti della conversazione...',
        empty: 'Nessun file e ancora allegato a questa conversazione.',
        downloadAction: 'Scarica',
        downloading: 'Download...',
        deleteAction: 'Elimina',
        deleteConfirm: 'Eliminare "{{fileName}}" da questa conversazione?'
      },
      notifications: {
        noSelectedServer: 'Nessun server LLM selezionato',
        noAgentProfile: 'Nessun profilo agente LLM'
      }
    },
    allComponents: {
      hero: {
        eyebrow: 'Tema Material globale',
        title: 'Palette scura, accento verde, superfici tecniche.',
        copy: 'Tutti i componenti Material ora si basano sulle variabili di `styles.scss` e su una tipografia monospace coerente con l’attuale stile prodotto.',
        summaryAria: 'Punti chiave del tema'
      },
      highlights: {
        dark: 'Scuro',
        monospace: 'Monospace',
        dense: 'Denso',
        highContrast: 'Alto contrasto'
      },
      sampleNotes: 'Pipeline CRM densa e pronta per l’uso quotidiano.',
      tabs: {
        form: 'Modulo',
        actions: 'Azioni'
      },
      form: {
        title: 'Contatto principale',
        subtitle: 'Verifica campi, pulsanti e selettori',
        company: 'Azienda',
        status: 'Stato',
        priority: 'Priorita',
        notes: 'Note',
        hint: 'Anche il testo di supporto e lo stato focus usano il tema personalizzato.',
        nda: 'NDA firmato',
        notifications: 'Avvisi pipeline',
        preferredChannel: 'Canale preferito',
        cancel: 'Annulla',
        saveDraft: 'Salva bozza',
        validate: 'Convalida'
      },
      state: {
        title: 'Stato attuale',
        subtitle: 'Lettura rapida dei token Material',
        notifications: 'Notifiche',
        channel: 'Canale'
      },
      actions: {
        title: 'Varianti pulsanti',
        text: 'Testo',
        outline: 'Contorno',
        primary: 'Primario',
        surface: 'Superficie'
      }
    },
    errors: {
      groups: {
        load: 'Impossibile caricare i gruppi.',
        required: 'Il nome del gruppo e obbligatorio.',
        selectionRequired: 'E obbligatorio selezionare un gruppo.',
        create: 'Impossibile creare il gruppo.',
        delete: 'Impossibile eliminare il gruppo selezionato.',
        addUser: 'Impossibile aggiungere l’utente al gruppo selezionato.',
        missingPayload: 'Il gruppo e stato creato senza payload.'
      },
      companies: {
        load: 'Impossibile caricare le aziende.',
        detail: 'Impossibile caricare i dettagli dell’azienda.',
        documentsTooLarge: 'Il documento aziendale selezionato supera il limite di 50 MB.',
        documentsUpload: 'Impossibile caricare il documento aziendale selezionato.',
        documentsDownload: 'Impossibile scaricare il documento aziendale selezionato.',
        documentsDelete: 'Impossibile eliminare il documento aziendale selezionato.',
        webLinksSave: 'Impossibile salvare i link web dell’azienda.'
      },
      offerings: {
        load: 'Impossibile caricare le offerte.',
        documentsTooLarge: 'Il documento dell’offerta selezionato supera il limite di 50 MB.',
        documentsLoad: 'Impossibile caricare i documenti dell’offerta selezionata.',
        documentsUpload: 'Impossibile caricare il documento selezionato per l’offerta.',
        documentsDownload: 'Impossibile scaricare il documento selezionato per l’offerta.',
        documentsDelete: 'Impossibile eliminare il documento selezionato per l’offerta.'
      },
      groupPortfolioAnalysis: {
        load: 'Impossibile caricare l’analisi di portafoglio del gruppo.',
        history: 'Impossibile caricare lo storico delle analisi di portafoglio del gruppo.',
        generate: 'Impossibile generare una nuova analisi di portafoglio del gruppo.'
      },
      admin: {
        documentsLoad: 'Impossibile caricare i file di conversazione dell’utente selezionato.',
        documentsDelete: 'Impossibile eliminare il file di conversazione selezionato per questo utente.',
        deletionPreviewLoad: 'Impossibile caricare l’anteprima di eliminazione dell’utente selezionato.',
        deleteUser: 'Impossibile eliminare il profilo utente selezionato.'
      },
      profile: {
        load: 'Impossibile caricare il tuo profilo.',
        save: 'Impossibile salvare il tuo profilo.',
        documentsLoad: 'Impossibile caricare i tuoi documenti pubblicati.',
        documentsDownload: 'Impossibile scaricare il documento di conversazione selezionato.',
        documentsDelete: 'Impossibile eliminare il documento di conversazione selezionato.',
        conversationsLoad: 'Impossibile caricare le tue conversazioni.',
        conversationsOpen: 'Impossibile aprire la conversazione selezionata.',
        conversationsDelete: 'Impossibile eliminare la conversazione selezionata.'
      },
      chat: {
        documentsTooLarge: 'Uno dei file selezionati supera il limite di 50 MB.',
        documentsUpload: 'Impossibile caricare i file selezionati per la conversazione.',
        documentsDownload: 'Impossibile scaricare il file selezionato della conversazione.',
        documentsDelete: 'Impossibile eliminare il file selezionato della conversazione.'
      },
      databaseAdmin: {
        loadBackups: 'Impossibile caricare i backup del database.',
        createBackup: 'Impossibile creare un backup del database.',
        downloadBackup: 'Impossibile scaricare il backup del database selezionato.',
        restoreBackup: 'Impossibile ripristinare il database dal backup caricato.'
      }
    }
  }
};
